// ==========================================================================
// Iron Rabbit — Floating Rich-Text Editor · Module 1
// --------------------------------------------------------------------------
// Self-contained, removable, mobile-first floating toolbar that attaches to
// the existing Expanded Text Editor (NoteModal) without touching its
// content model, storage, or the existing "T | Tags | HTML | ?" top bar.
//
// SCOPE (module 1):
//   • Draggable via a dedicated 3-bar grip on the LEFT edge only.
//   • Horizontal (default) OR vertical (90° rotated) orientation.
//   • Inner tool row is independently scrollable — scroll never repositions
//     the toolbar (only the grip does).
//   • ~15 real tools: H1/H2/H3, B/I/U/S, bulleted/numbered/checklist,
//     Undo, Redo, Link, Image, 🌳 Hierarchy, 📋 Insert Form.
//     Everything else from the 35-tool spec is a stub with a "coming soon"
//     toast and a stable data-testid, so future wiring is a one-liner.
//   • Formatting hooks into the SAME contentEditable that NoteModal already
//     renders (`[data-testid="note-content-input-html"]`) via
//     `document.execCommand`.  When the editor is in plain textarea mode
//     (`[data-testid="note-content-input"]`) tools fall back to markdown-
//     style surround-selection insertion so nothing throws.
//   • Uses `window.visualViewport` to sit just above the on-screen keyboard
//     on mobile.  Falls back to bottom:16px on desktop.
//   • Zero external state, zero new persistence, zero Quick Guide changes,
//     zero routing changes, zero storage changes.
// ==========================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Menu, ArrowUpDown, ArrowLeftRight,
  Heading1, Heading2, Heading3, Type, CaseSensitive, Pilcrow,
  Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, ListChecks,
  Undo2, Redo2,
  Link as LinkIcon, Image as ImageIcon,
  Network, ClipboardList,
  CornerDownLeft, WrapText,
  // Stubbed set
  AlignLeft, AlignCenter, AlignRight, Rows3, Palette, Highlighter, Scissors, Copy, ClipboardPaste,
  ScanLine, Camera, Mic, FileText, Printer, Share2,
} from "lucide-react";
import { FormsPackAdapter, HierarchyAdapter } from "./adapters";
import { plainTextToHtml, sanitizeHtml, normalizeWrapBoxSpacingDom, markWrapSeparators, markWrapSeparatorsDom } from "../../utils/htmlSanitize";

// --------------------------------------------------------------------------
// Small helper — find the active editor element (contentEditable or
// textarea) associated with NoteModal OR the FullScreenNote editor.  We
// probe the DOM instead of taking a ref prop so the toolbar remains 100%
// removable without editing either host's internal state shape.
//
// The toolbar binds to whichever Expanded Text Editor is currently
// mounted:
//   • NoteModal      → "note-content-input"  (textarea)
//                      "note-content-input-html" (contentEditable)
//   • FullScreenNote → "fullscreen-content-input"        (textarea, text mode)
//                      "fullscreen-content-input-html"   (textarea, html mode)
//                      "fullscreen-content-input-format" (contentEditable)
// Priority order favours the HTML/format contentEditable because that is
// where rich formatting actually renders.
// --------------------------------------------------------------------------
const EDITOR_TESTIDS = [
  // contentEditable targets first — rich-text operations prefer these
  "note-content-input-html",
  "fullscreen-content-input-format",
  // textarea targets second (plaintext / raw-html editing)
  "note-content-input",
  "fullscreen-content-input",
  "fullscreen-content-input-html",
];
const EDITOR_SELECTOR = EDITOR_TESTIDS
  .map((id) => `[data-testid="${id}"]`)
  .join(",");

function findActiveEditor() {
  const active = document.activeElement;
  if (active && (active.isContentEditable || active.tagName === "TEXTAREA")) {
    const id = active.getAttribute && active.getAttribute("data-testid");
    if (id && EDITOR_TESTIDS.includes(id)) return active;
  }
  // Fallback: priority order from EDITOR_TESTIDS — first match wins.
  for (const id of EDITOR_TESTIDS) {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) return el;
  }
  return null;
}

// Find the active contentEditable editor (NoteModal `note-content-input-html`
// or FullScreenNote `fullscreen-content-input-format`). Used by the history
// manager and checklist-click delegation, both of which only make sense on
// the rich-text surface.
function findActiveHtmlEditor() {
  return (
    document.querySelector('[data-testid="note-content-input-html"]') ||
    document.querySelector('[data-testid="fullscreen-content-input-format"]') ||
    null
  );
}

// --------------------------------------------------------------------------
// Formatting primitives — REAL rich-text only.
// Two paths:
//   1) Editor is <textarea> (plain-text mode). Promote the note to HTML by
//      wrapping the selected range in a real HTML tag (<strong>/<em>/<u>/
//      <s>) or converting the current line into a <h1>/<h2>/<h3>. The
//      promoted HTML string is written back through React's native value
//      setter so NoteModal's `onChange` fires, `looksLikeHtml(content)`
//      flips to true on the next render, and the modal automatically
//      swaps textarea → contentEditable. We refocus the new element after
//      paint so subsequent formatting operates on the live contentEditable.
//   2) Editor is contentEditable (HTML mode). Use native execCommand,
//      which produces real <strong>/<em>/<u>/<s>/<h1..3> nodes that the
//      existing sanitiser + storage layer already handle.
// No Markdown markers (**, __, ~~, ##, etc.) are ever inserted anywhere.
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// HistoryStack — custom multi-level undo/redo for the HTML editor.
//
// `document.execCommand("undo"/"redo")` traverses the browser's own stack,
// which (a) does not include programmatic `innerHTML = …` writes (used by
// the textarea → HTML promotion in this toolbar), and (b) has no API for
// snapshotting formatting commands. Per spec §9/§10 we need a stack that:
//   • groups continuous typing (debounced snapshot) into one entry
//   • records an immediate snapshot before + after every toolbar command
//   • supports many undo/redo levels (cap at 200 to bound memory)
//   • discards the redo branch when the user edits after an undo
//   • restores both innerHTML AND a serialized caret location
// Caret location is serialized as a path through child-node indices +
// offset — robust across innerHTML swaps.
// --------------------------------------------------------------------------
function serializeSelection(root) {
  try {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const path = (node) => {
      const indices = [];
      let n = node;
      while (n && n !== root) {
        const parent = n.parentNode;
        if (!parent) return null;
        indices.unshift(Array.prototype.indexOf.call(parent.childNodes, n));
        n = parent;
      }
      return indices;
    };
    return {
      startPath: path(range.startContainer),
      startOffset: range.startOffset,
      endPath: path(range.endContainer),
      endOffset: range.endOffset,
    };
  } catch { return null; }
}
function restoreSelection(root, saved) {
  if (!saved) return;
  try {
    const resolve = (indices) => {
      let n = root;
      for (const i of indices) {
        if (!n || !n.childNodes || !n.childNodes[i]) return null;
        n = n.childNodes[i];
      }
      return n;
    };
    const startNode = resolve(saved.startPath);
    const endNode = resolve(saved.endPath);
    if (!startNode || !endNode) return;
    const sel = window.getSelection();
    const r = document.createRange();
    const sLen = (startNode.textContent || "").length;
    const eLen = (endNode.textContent || "").length;
    r.setStart(startNode, Math.min(saved.startOffset, sLen));
    r.setEnd(endNode, Math.min(saved.endOffset, eLen));
    sel.removeAllRanges();
    sel.addRange(r);
  } catch { /* fail silent — caret just goes to end */ }
}

class HistoryStack {
  constructor(max = 200) {
    this.stack = [];
    this.pointer = -1;
    this.max = max;
    this._timer = null;
    this._pending = null;
  }
  _trim() {
    if (this.stack.length > this.max) {
      const drop = this.stack.length - this.max;
      this.stack.splice(0, drop);
      this.pointer -= drop;
    }
  }
  _push(entry) {
    // Duplicate guard FIRST — before the redo-branch splice. Otherwise
    // the synthetic `input` event dispatched after Undo/Redo triggers
    // `snapshotDebounced` → a pending snapshot equal to the current
    // pointer's state. On the next Redo (or any later `_push`),
    // `splice(pointer+1)` would destroy the redo branch and only then
    // discover the duplicate, so the subsequent Redo turns into a
    // no-op even though the branch should still exist.
    const last = this.stack[this.pointer];
    if (last && last.html === entry.html) return;
    // Discard any redo branch beyond the current pointer.
    this.stack.splice(this.pointer + 1);
    this.stack.push(entry);
    this.pointer = this.stack.length - 1;
    this._trim();
  }
  flush() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    if (this._pending) { this._push(this._pending); this._pending = null; }
  }
  snapshotImmediate(root) {
    this.flush();
    this._push({ html: root.innerHTML, sel: serializeSelection(root) });
  }
  // Called from input events — debounces to group continuous typing.
  snapshotDebounced(root, delay = 800) {
    this._pending = { html: root.innerHTML, sel: serializeSelection(root) };
    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      if (this._pending) { this._push(this._pending); this._pending = null; }
      this._timer = null;
    }, delay);
  }
  canUndo() { return this.pointer > 0; }
  canRedo() { return this.pointer < this.stack.length - 1; }
  undo() { this.flush(); if (!this.canUndo()) return null; this.pointer--; return this.stack[this.pointer]; }
  redo() { this.flush(); if (!this.canRedo()) return null; this.pointer++; return this.stack[this.pointer]; }
  resetTo(html, sel) {
    this.stack = [{ html, sel }];
    this.pointer = 0;
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    this._pending = null;
  }
}


// React tracks the last-known value on inputs/textareas. Setting `.value`
// directly bypasses that tracker so NoteModal's controlled `onChange`
// never fires. Using the native prototype setter is the standard React
// escape hatch for programmatic value writes.
function reactSetValue(el, next) {
  try {
    const proto = window.HTMLTextAreaElement && window.HTMLTextAreaElement.prototype;
    const desc = proto && Object.getOwnPropertyDescriptor(proto, "value");
    if (desc && typeof desc.set === "function") {
      desc.set.call(el, next);
    } else {
      el.value = next;
    }
  } catch {
    el.value = next;
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function runExecCommand(editor, command, value = null) {
  try {
    // Fix E.1 — avoid Android-Chrome's selection-collapse-on-focus bug.
    // Calling `.focus()` on an already-focused contentEditable collapses
    // the active selection to offset 0 on Android Chrome / Capacitor
    // WebViews. For `formatBlock` this is harmless (it operates on the
    // containing block, not the selection range) which is why H1/H2/H3
    // feels reliable. For `bold`/`italic`/`underline` which DO operate
    // on the range, the collapse turns the command into a pending-state
    // toggle with no visible effect — forcing the user to tap again.
    // We only re-focus when focus has genuinely drifted elsewhere.
    if (editor && typeof editor.focus === "function" && document.activeElement !== editor) {
      editor.focus();
    }
    // execCommand is deprecated but universally supported and still the
    // simplest cross-browser bridge to contentEditable formatting.
    document.execCommand(command, false, value);
    // Fire input event so React-controlled contentEditables observing
    // change propagate correctly.
    editor?.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch {
    return false;
  }
}

// --------------------------------------------------------------------------
// Fix E.2 — selection stashing for Android touch events.
//
// Even with `onPointerDown.preventDefault()` on every tool button,
// Android Chrome's `touchstart` can transfer focus BEFORE our pointer
// handler fires (focus-follows-tap heuristic). By the time `click`
// synthesises and `applyFormat` runs, the editor's selection has been
// collapsed to offset 0 and `document.execCommand("bold")` silently
// no-ops against the user's original range.
//
// Fix: stash the editor's selection at the earliest moment we can get
// our hands on it — the button's `onPointerDown`, which runs BEFORE
// touchstart's default actions are committed on most Android builds.
// `applyFormat` then checks whether the live selection has drifted and,
// if so, restores the stashed range before dispatching the command.
//
// The stash is keyed by the editor element so multiple
// `FloatingRichTextToolbar` instances (NoteModal + FullScreenNote) don't
// clobber each other. On textarea we rely on the browser's native
// selectionStart/End preservation and skip the stash.
// --------------------------------------------------------------------------
const SELECTION_STASH = { editor: null, range: null, start: 0, end: 0 };

function stashSelection() {
  const editor = findActiveEditor();
  if (!editor) return;
  if (editor.tagName === "TEXTAREA") {
    // Textarea retains selectionStart/End across focus loss natively —
    // no manual stash is needed. We still record the editor pointer so
    // `restoreStashedSelectionIfNeeded` knows which element the stash
    // applies to.
    SELECTION_STASH.editor = editor;
    SELECTION_STASH.range = null;
    SELECTION_STASH.start = editor.selectionStart ?? 0;
    SELECTION_STASH.end = editor.selectionEnd ?? 0;
    return;
  }
  try {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    SELECTION_STASH.editor = editor;
    SELECTION_STASH.range = range.cloneRange();
  } catch { /* noop */ }
}

function restoreStashedSelectionIfNeeded(editor) {
  if (!editor) return;
  if (SELECTION_STASH.editor !== editor) return;
  if (editor.tagName === "TEXTAREA") return; // native preservation
  if (!SELECTION_STASH.range) return;
  const sel = window.getSelection && window.getSelection();
  if (!sel) return;
  const live = sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
  // Restore only when the live selection has drifted — collapsed when it
  // was previously a range, or landed outside the editor entirely. If
  // the user still has an intact selection in the editor, leave it alone.
  const stashedWasRange = !SELECTION_STASH.range.collapsed;
  const liveIsBroken =
    !live
    || !editor.contains(live.commonAncestorContainer)
    || (stashedWasRange && live.collapsed);
  if (!liveIsBroken) return;
  try {
    // Guard: the stashed range nodes may have been removed by a prior
    // DOM mutation (e.g. React re-render). Verify the common ancestor
    // is still attached inside the editor before restoring.
    if (!editor.contains(SELECTION_STASH.range.commonAncestorContainer)) return;
    sel.removeAllRanges();
    sel.addRange(SELECTION_STASH.range);
  } catch { /* noop */ }
}

// --------------------------------------------------------------------------
// Deterministic tag wrap (used for Strikethrough).
//
// `document.execCommand("strikeThrough")` on the user's Android Chrome
// nests the strike element inside any existing formatting ancestor, so
// applying Strike to text that overlaps a <u> region — or that had
// underline mode toggled on for the selection — produces
//   <u><strike>...</strike></u>
// which renders as BOTH underline AND line-through. Empirically
// reproduced in this environment: after `execCommand('underline')`
// followed by `execCommand('strikeThrough')` on the same range, we get
// `<u><strike>hello</strike></u>`.
//
// The fix: bypass execCommand for Strike and wrap the selection in a
// fresh <s> node via a direct Range mutation. This is browser-agnostic,
// doesn't inherit ambient command modes, and produces plain
// `<s>text</s>` markup that the sanitiser accepts and the CSS renders
// as line-through ONLY. Underline is completely untouched.
// --------------------------------------------------------------------------
// Fix I — reliable format-first-then-type for inline formats in
// contentEditable mode.
//
// Problem: `document.execCommand("bold")` on a collapsed caret relies
// on the browser's native "pending inline formatting" state. Chrome
// desktop supports this correctly (verified in preview), but Capacitor
// Android WebView drops the pending state before the next keystroke
// lands, so the user sees the Bold button light up but their typing
// stays plain.
//
// Fix: skip native pending-state for inline collapsed-caret taps and
// instead insert a `<tag class="ir-pending-inline">\u200B</tag>` marker
// at the caret. The caret lands AFTER the ZWSP, inside the tag. When
// the user types, the characters land inside the tag — pending-state
// is implicit in the DOM structure instead of a browser flag that
// Capacitor can forget.
//
// On the first `input` event that lengthens the marker beyond the
// ZWSP, a document-level listener strips the ZWSP and the
// `.ir-pending-inline` class, leaving a clean `<strong>text</strong>`
// (or em / u / s).
//
// Toggle-off: if the user taps the same inline format while the caret
// is still inside a pending marker (they changed their mind before
// typing), we unwrap the marker and leave the caret where it was.
//
// This helper is INLINE-only (bold / italic / underline / strike).
// H1 / H2 / H3 continue through the unchanged formatBlock path.
const PENDING_INLINE_CLASS = "ir-pending-inline";

function insertInlinePendingMarker(editor, format) {
  const tag = INLINE_TAG[format];
  if (!tag || !editor) return false;
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!range.collapsed) return false;
  if (!editor.contains(range.startContainer)) return false;

  // --------------------------------------------------------------------
  // Toggle-off: find the NEAREST ancestor matching this format (any —
  // pending-marker class OR a committed <strong>/<em>/<u>/<s>). If the
  // caret already lives in such an ancestor, the user wants to exit it:
  //   • Pending marker + still only a ZWSP inside → unwrap the marker
  //     entirely (user tapped format twice, no text committed yet).
  //   • Committed inline tag with real text → move the caret to the
  //     position immediately AFTER the ancestor in its parent, so the
  //     next keystrokes land in plain text. The committed tag stays
  //     intact — this matches Chrome/Google-Docs toggle-off behaviour.
  // --------------------------------------------------------------------
  const matchTags = { bold: ["B", "STRONG"], italic: ["I", "EM"], underline: ["U"], strike: ["S", "STRIKE", "DEL"] };
  const targetTags = matchTags[format] || [];
  let node = range.startContainer;
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  let ancestor = null;
  while (node && node !== editor) {
    if (node.tagName && targetTags.includes(node.tagName)) { ancestor = node; break; }
    node = node.parentElement;
  }
  if (ancestor) {
    try {
      const isPending = ancestor.classList && ancestor.classList.contains(PENDING_INLINE_CLASS);
      const txt = (ancestor.textContent || "");
      const onlyZwsp = txt === ZWSP || txt === "";
      const parent = ancestor.parentNode;
      if (!parent) return false;
      if (isPending && onlyZwsp) {
        // Pure pending-marker toggle: remove it, caret where it was.
        const indexInParent = Array.prototype.indexOf.call(parent.childNodes, ancestor);
        parent.removeChild(ancestor);
        const nr = document.createRange();
        const safeIdx = Math.min(indexInParent, parent.childNodes.length);
        nr.setStart(parent, safeIdx);
        nr.setEnd(parent, safeIdx);
        sel.removeAllRanges(); sel.addRange(nr);
      } else {
        // Committed inline: exit by moving caret to just after the
        // ancestor in its parent. The user's prior formatted text is
        // preserved verbatim. We also insert a zero-width space node
        // sibling after the ancestor so the caret has a stable text
        // anchor to live in — plain contentEditable sometimes "sticks"
        // the caret back inside the ancestor without this hint. The
        // ZWSP is stripped on the next real keystroke by
        // `cleanupPendingInlineMarkers` (which iterates all pending
        // markers; we also run a tiny inline cleanup for stray exit
        // anchors below).
        const exitAnchor = document.createTextNode(ZWSP);
        exitAnchor.__irExitAnchor = true; // marker for cleanup
        if (ancestor.nextSibling) parent.insertBefore(exitAnchor, ancestor.nextSibling);
        else parent.appendChild(exitAnchor);
        const nr = document.createRange();
        nr.setStart(exitAnchor, 1);
        nr.setEnd(exitAnchor, 1);
        sel.removeAllRanges(); sel.addRange(nr);
      }
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    } catch { return false; }
  }

  // --------------------------------------------------------------------
  // Format-switch: when the caret sits inside ANY inline-format
  // ancestor (same or different format, pending-or-committed), the new
  // format must become a SIBLING of that ancestor — not a nested child.
  // This implements "selecting the next formatting tool deactivates the
  // previous typing format" for the format-first-then-type workflow
  // (collapsed caret only; existing-text selection is handled above and
  // by wrapSelectionWithTag).
  //
  //   • Outermost ancestor is empty-pending (only ZWSP, pending class)
  //     → dissolve it; caret takes its slot.
  //   • Outermost ancestor has committed text → leave it intact; caret
  //     emerges to the position IMMEDIATELY AFTER it in its parent.
  //     Any empty-pending descendants inside are removed so we don't
  //     leave stale <em>​</em> fragments behind.
  //
  // The subsequent "fresh insert" block will then create a clean
  // sibling marker and position the caret inside it.
  // --------------------------------------------------------------------
  const INLINE_FMT_TAGS = { B: 1, STRONG: 1, I: 1, EM: 1, U: 1, S: 1, STRIKE: 1, DEL: 1 };
  try {
    let walk = range.startContainer;
    if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
    let outermost = null;
    while (walk && walk !== editor && walk.tagName && INLINE_FMT_TAGS[walk.tagName]) {
      outermost = walk;
      walk = walk.parentElement;
    }
    if (outermost) {
      const parent = outermost.parentNode;
      if (parent) {
        const outerText = outermost.textContent || "";
        const outerIsPending = outermost.classList && outermost.classList.contains(PENDING_INLINE_CLASS);
        const isOutermostEmpty = (outerText === ZWSP || outerText === "") && outerIsPending;
        let newIdx;
        if (isOutermostEmpty) {
          const outermostIdx = Array.prototype.indexOf.call(parent.childNodes, outermost);
          parent.removeChild(outermost);
          newIdx = outermostIdx;
        } else {
          // Clean up any empty pending descendants (noise left over from
          // a previous same-sequence tap, e.g. the empty <em>​</em>
          // left inside <b>Bold</b> when user had tapped I in-between).
          const pendingInside = Array.from(outermost.querySelectorAll("." + PENDING_INLINE_CLASS));
          pendingInside.forEach((m) => {
            const t = m.textContent || "";
            if (t === ZWSP || t === "") {
              try { m.parentNode && m.parentNode.removeChild(m); } catch {}
            }
          });
          const outermostIdx = Array.prototype.indexOf.call(parent.childNodes, outermost);
          newIdx = outermostIdx + 1;
        }
        const safeIdx = Math.min(Math.max(newIdx, 0), parent.childNodes.length);
        const nr = document.createRange();
        nr.setStart(parent, safeIdx);
        nr.setEnd(parent, safeIdx);
        sel.removeAllRanges(); sel.addRange(nr);
      }
    }
  } catch { /* fall through to fresh insert */ }

  // --------------------------------------------------------------------
  // Fresh insert: no matching ancestor → create a pending marker at the
  // caret, position the caret inside AFTER the ZWSP. User keystrokes
  // land inside the marker — formatted.
  // --------------------------------------------------------------------
  try {
    // Re-read the (possibly-updated) range after format-switch dissolve.
    const freshRange = sel.rangeCount > 0 ? sel.getRangeAt(0) : range;
    const marker = document.createElement(tag);
    marker.setAttribute("class", PENDING_INLINE_CLASS);
    marker.appendChild(document.createTextNode(ZWSP));
    freshRange.insertNode(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1);
    nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch { return false; }
}

// --------------------------------------------------------------------------
// Aa Text Appearance — size + color classes applied via `<span>` markers.
// Lives alongside the inline-format system but on a SEPARATE axis so it
// never interferes with Bold / Italic / Underline / Strike state.
//
// Design:
//   • Each tap on a size or color option in the Aa dropdown applies ONE
//     class on its axis (size XOR color). Classes stack across axes so
//     "Large + Blue" produces `<span class="ir-size-lg ir-color-blue">`.
//   • When the caret is inside an EMPTY `<span>` pending marker (user
//     just picked an appearance but hasn't typed yet), we mutate it
//     in-place: swap the class on that axis, keeping the other axis
//     intact. The pending marker also stays empty so the user can keep
//     refining before typing.
//   • When the caret is inside a `<span>` with committed text, we emerge
//     to a sibling position AFTER it (so new text with new appearance
//     doesn't retroactively affect already-typed text) and insert a
//     fresh pending `<span>`.
//   • When the caret is inside a non-span inline-format ancestor
//     (<b>/<i>/<u>/<s>), the appearance `<span>` nests inside — size +
//     color stack with Bold/Italic/Underline/Strike (different axes).
//   • cleanupPendingInlineMarkers (below) already strips ONLY the
//     `ir-pending-inline` class once content > 1 char, so size/color
//     classes survive the commit — no changes needed there.
// --------------------------------------------------------------------------
const AA_SIZE_CLASSES = ["ir-size-sm", "ir-size-md", "ir-size-lg", "ir-size-xl"];
const AA_COLOR_CLASSES = [
  "ir-color-white", "ir-color-gray", "ir-color-red", "ir-color-orange",
  "ir-color-amber", "ir-color-yellow", "ir-color-lime", "ir-color-green",
  "ir-color-teal", "ir-color-cyan", "ir-color-sky", "ir-color-blue",
  "ir-color-indigo", "ir-color-violet", "ir-color-pink", "ir-color-rose",
];

function findAncestorPendingAppearanceSpan(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.classList && walk.classList.contains(PENDING_INLINE_CLASS)) {
      return walk;
    }
    walk = walk.parentElement;
  }
  return null;
}

function findAncestorCommittedAppearanceSpan(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.classList) {
      // Has an ir-size-* OR ir-color-* class and isn't pending
      let hasAppearance = false;
      walk.classList.forEach((c) => {
        if (c.indexOf("ir-size-") === 0 || c.indexOf("ir-color-") === 0) hasAppearance = true;
      });
      if (hasAppearance && !walk.classList.contains(PENDING_INLINE_CLASS)) return walk;
    }
    walk = walk.parentElement;
  }
  return null;
}

// Apply exactly ONE change on ONE axis. `axis` is "size" or "color".
// `className` is the CSS class to apply (e.g. "ir-size-lg", "ir-color-blue")
// or null to CLEAR the axis.
//
// Axis semantics (post-v205):
//   • SIZE axis is DOCUMENT-LEVEL. The class lives on the editor root
//     element so the whole typographic hierarchy (P / H1 / H2 / H3 and
//     every inline run) scales together via `em`-based CSS. There is no
//     per-span size wrapper for new picks.
//   • COLOR axis is PER-SPAN. Each color pick wraps the format-first
//     caret in a `<span class="ir-pending-inline ir-color-X">` marker
//     exactly as before (text-run-specific color).
//
// `ownedSpan` is only consulted for the color path (stacking vs orphan
// detection). It is ignored for the size path.
function applyAppearanceChange(editor, axis, className, ownedSpan) {
  if (!editor) return false;
  // ---- SIZE axis: document-level ---------------------------------------
  if (axis === "size") {
    // Textarea has no root class to carry an inline size — promote to
    // HTML (empty pending marker, no inline class), then apply the
    // size class to the HTML editor root once it mounts.
    if (editor.tagName === "TEXTAREA") {
      const mode = promoteTextareaWithAppearance(editor, null);
      if (!mode) return false;
      focusHtmlEditorSoon(mode);
      // Deferred: after React swaps the textarea for the HTML editor,
      // locate the editor root and apply the size class.
      const applySoon = (attempts) => {
        const htmlEd = findActiveEditor();
        if (htmlEd && htmlEd.tagName !== "TEXTAREA" && htmlEd.classList) {
          AA_SIZE_CLASSES.forEach((c) => htmlEd.classList.remove(c));
          if (className) htmlEd.classList.add(className);
          htmlEd.dispatchEvent(new Event("input", { bubbles: true }));
          return;
        }
        if (attempts > 0) setTimeout(() => applySoon(attempts - 1), 20);
      };
      setTimeout(() => applySoon(8), 20);
      return true;
    }
    // HTML mode — apply directly to the editor root.
    try { if (document.activeElement !== editor) editor.focus(); } catch {}
    if (editor.classList) {
      AA_SIZE_CLASSES.forEach((c) => editor.classList.remove(c));
      if (className) editor.classList.add(className);
    }
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }
  // ---- COLOR axis: per-span, format-first-then-type -------------------
  // Textarea → promote to contentEditable + inject a pending appearance
  // span. Mirrors how Bold/Italic/Underline/Strike handle this case.
  if (editor.tagName === "TEXTAREA") {
    const mode = promoteTextareaWithAppearance(editor, className);
    if (mode) focusHtmlEditorSoon(mode);
    return !!mode;
  }
  try { if (document.activeElement !== editor) editor.focus(); } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return false;

  const axisClasses = axis === "size" ? AA_SIZE_CLASSES : AA_COLOR_CLASSES;
  const stripAxis = (el) => {
    if (!el || !el.classList) return;
    axisClasses.forEach((c) => el.classList.remove(c));
  };

  // ---- Selection path (user has non-collapsed selection) ----
  if (!range.collapsed) {
    // Wrap the selection in a fresh <span class="ir-size-X"> or add the
    // class to an existing wrapper. Simplest + safest approach: use a
    // Range.surroundContents when possible, else fall back to extract +
    // wrap. Pending-class is NOT applied here — this is a committed op.
    try {
      const span = document.createElement("span");
      if (className) span.classList.add(className);
      try {
        range.surroundContents(span);
      } catch {
        // Range spans multiple nodes — use extract+wrap
        const frag = range.extractContents();
        span.appendChild(frag);
        range.insertNode(span);
      }
      // Place caret just after the wrapped span so the user can continue
      const nr = document.createRange();
      const parent = span.parentNode;
      if (parent) {
        const idx = Array.prototype.indexOf.call(parent.childNodes, span);
        nr.setStart(parent, idx + 1);
        nr.setEnd(parent, idx + 1);
        sel.removeAllRanges(); sel.addRange(nr);
      }
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    } catch { return false; }
  }

  // ---- Collapsed caret path (format-first-then-type) ----
  const pending = findAncestorPendingAppearanceSpan(editor, range.startContainer);
  if (pending) {
    const text = pending.textContent || "";
    const isEmpty = text === ZWSP || text === "";
    if (isEmpty) {
      // Distinguish STACKING vs ORPHAN:
      //   • Owned by this panel session → stack (strip same axis, keep
      //     opposite axis, add the new class).
      //   • Not owned (Enter-cloned from a previous line, or any other
      //     span the panel hasn't touched) → reset BOTH axes so the
      //     user's single pick doesn't silently inherit stale state
      //     from before. This is the fix that makes Size and Color
      //     truly independent properties.
      const isOwned = ownedSpan && (pending === ownedSpan);
      if (isOwned) {
        stripAxis(pending);
        if (className) pending.classList.add(className);
      } else {
        AA_SIZE_CLASSES.forEach((c) => pending.classList.remove(c));
        AA_COLOR_CLASSES.forEach((c) => pending.classList.remove(c));
        if (className) pending.classList.add(className);
      }
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    }
    // Pending span has committed text — emerge to a sibling AFTER it.
    const parent = pending.parentNode;
    if (!parent) return false;
    const idx = Array.prototype.indexOf.call(parent.childNodes, pending);
    const marker = document.createElement("span");
    marker.classList.add(PENDING_INLINE_CLASS);
    if (className) marker.classList.add(className);
    marker.appendChild(document.createTextNode(ZWSP));
    if (pending.nextSibling) parent.insertBefore(marker, pending.nextSibling);
    else parent.appendChild(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1);
    nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  // Caret inside a committed appearance span — emerge to a sibling AFTER
  // it so the new appearance doesn't retroactively affect typed text.
  const committed = findAncestorCommittedAppearanceSpan(editor, range.startContainer);
  if (committed) {
    const parent = committed.parentNode;
    if (!parent) return false;
    const marker = document.createElement("span");
    marker.classList.add(PENDING_INLINE_CLASS);
    if (className) marker.classList.add(className);
    marker.appendChild(document.createTextNode(ZWSP));
    if (committed.nextSibling) parent.insertBefore(marker, committed.nextSibling);
    else parent.appendChild(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1);
    nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  // Fresh insert at caret — no appearance ancestor at all.
  try {
    const marker = document.createElement("span");
    marker.classList.add(PENDING_INLINE_CLASS);
    if (className) marker.classList.add(className);
    marker.appendChild(document.createTextNode(ZWSP));
    range.insertNode(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1);
    nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch { return false; }
}

// Report the CURRENT appearance active at the caret so the Aa dropdown
// can highlight the right chip/swatch. Size reads from the EDITOR ROOT
// (document-level); color walks ancestor spans (per-run).
function readActiveAppearance(editor) {
  const result = { size: null, color: null };
  if (!editor) return result;
  // Size — editor root class.
  if (editor.classList) {
    AA_SIZE_CLASSES.forEach((c) => {
      if (editor.classList.contains(c)) result.size = c;
    });
  }
  // Color — ancestor span walk.
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return result;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return result;
  let walk = range.startContainer;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.classList) {
      walk.classList.forEach((c) => {
        if (!result.color && c.indexOf("ir-color-") === 0) result.color = c;
      });
    }
    walk = walk.parentElement;
  }
  return result;
}

// --------------------------------------------------------------------------
// Highlighter — text BACKGROUND color. Follows the exact same per-span
// mechanism as Aa Color: wraps the current selection in a span (or
// inserts a pending `<span class="ir-pending-inline ir-hl-X">` at the
// caret for format-first-then-type). Fully independent axis from the
// Aa Size (document-level) and Aa Color (foreground) systems — all
// three can stack on the same text run.
// --------------------------------------------------------------------------
const HL_CLASSES = [
  "ir-hl-white", "ir-hl-gray", "ir-hl-red", "ir-hl-orange",
  "ir-hl-amber", "ir-hl-yellow", "ir-hl-lime", "ir-hl-green",
  "ir-hl-teal", "ir-hl-cyan", "ir-hl-sky", "ir-hl-blue",
  "ir-hl-indigo", "ir-hl-violet", "ir-hl-pink", "ir-hl-rose",
];

function findAncestorHighlightSpan(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.classList) {
      let has = false;
      walk.classList.forEach((c) => { if (c.indexOf("ir-hl-") === 0) has = true; });
      if (has) return walk;
    }
    walk = walk.parentElement;
  }
  return null;
}

function readActiveHighlight(editor) {
  if (!editor) return null;
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  let walk = range.startContainer;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.classList) {
      let found = null;
      walk.classList.forEach((c) => { if (!found && c.indexOf("ir-hl-") === 0) found = c; });
      if (found) return found;
    }
    walk = walk.parentElement;
  }
  return null;
}

// Apply a highlight. `className` is a `ir-hl-*` class (or null to
// clear). Behaviour mirrors Aa Color's "collapsed caret = pending
// marker, non-empty selection = wrap selection".
function applyHighlight(editor, className) {
  if (!editor) return false;
  // Textarea → promote to HTML with a pending highlight span.
  if (editor.tagName === "TEXTAREA") {
    const mode = promoteTextareaWithAppearance(editor, className);
    if (mode) focusHtmlEditorSoon(mode);
    return !!mode;
  }
  // CRITICAL ORDERING FIX (Highlighter repair, 2026-02-28):
  //
  // Previously this function focused the editor and then IMMEDIATELY
  // read the live Selection. On Android Chrome and Capacitor WebViews,
  // calling `.focus()` on an already-focused contentEditable collapses
  // the active selection to offset 0 (same bug called out in
  // `runExecCommand`). That meant: when the user had text selected,
  // tapped Highlighter → palette → a swatch, the swatch's click fired
  // AFTER the panel overlay had already blurred the editor's visible
  // selection — the stash restored it correctly in `applyOnEditor`,
  // but THIS function then focus()'d the editor a second time and
  // collapsed the just-restored range before `sel.getRangeAt(0)` ran.
  // Net effect: `range.collapsed === true`, non-collapsed path was
  // skipped, pending-marker path ran, nothing visibly highlighted.
  //
  // Fix: focus FIRST, then restore the stash AFTER the focus() has
  // done its damage, THEN read the live Selection. This mirrors the
  // defence used by every other format tool via `applyFormat`.
  try { if (document.activeElement !== editor) editor.focus(); } catch {}
  try { restoreStashedSelectionIfNeeded(editor); } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return false;

  const stripHl = (el) => {
    if (!el || !el.classList) return;
    HL_CLASSES.forEach((c) => el.classList.remove(c));
  };

  // ---- Non-collapsed selection → wrap/update directly ----
  if (!range.collapsed) {
    // If the whole selection is inside a single highlight span, just
    // swap/clear its hl class in place (no new wrapping needed).
    const commonAncestor = range.commonAncestorContainer;
    const existing = findAncestorHighlightSpan(editor, commonAncestor);
    if (existing && existing.contains(range.startContainer) && existing.contains(range.endContainer)) {
      stripHl(existing);
      if (className) existing.classList.add(className);
      if (existing.classList.length === 0) existing.removeAttribute("class");
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    }
    // Otherwise wrap the selection in a fresh span.
    try {
      const span = document.createElement("span");
      if (className) span.classList.add(className);
      try {
        range.surroundContents(span);
      } catch {
        const frag = range.extractContents();
        span.appendChild(frag);
        range.insertNode(span);
      }
      // Caret just after the wrapped span so the user can continue typing.
      const nr = document.createRange();
      const parent = span.parentNode;
      if (parent) {
        const idx = Array.prototype.indexOf.call(parent.childNodes, span);
        nr.setStart(parent, idx + 1);
        nr.setEnd(parent, idx + 1);
        sel.removeAllRanges(); sel.addRange(nr);
      }
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    } catch { return false; }
  }

  // ---- Collapsed caret (format-first-then-type) ----
  const pending = findAncestorPendingAppearanceSpan(editor, range.startContainer);
  if (pending) {
    const text = pending.textContent || "";
    const isEmpty = text === ZWSP || text === "";
    if (isEmpty) {
      stripHl(pending);
      if (className) pending.classList.add(className);
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    }
    // Pending has committed text → emerge sibling.
    const parent = pending.parentNode;
    if (!parent) return false;
    const marker = document.createElement("span");
    marker.classList.add(PENDING_INLINE_CLASS);
    if (className) marker.classList.add(className);
    marker.appendChild(document.createTextNode(ZWSP));
    if (pending.nextSibling) parent.insertBefore(marker, pending.nextSibling);
    else parent.appendChild(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1); nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }
  // Fresh insert.
  try {
    const marker = document.createElement("span");
    marker.classList.add(PENDING_INLINE_CLASS);
    if (className) marker.classList.add(className);
    marker.appendChild(document.createTextNode(ZWSP));
    range.insertNode(marker);
    const nr = document.createRange();
    nr.setStart(marker.firstChild, 1); nr.setEnd(marker.firstChild, 1);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch { return false; }
}

// Toggle the Highlighter OFF at the current caret. Called when the user
// taps the Highlighter button while `active.hl` is already true. It
// MUST NOT remove highlights elsewhere in the document — only exit the
// highlight state at the caret so continuing to type produces plain
// (non-highlighted) text. Mirrors the same "exit anchor" strategy used
// when toggling off any committed inline format: insert a ZWSP sibling
// after the highlight span and move the caret there. For a still-
// pending highlight marker (ZWSP-only, no real text yet), simply strip
// the ir-hl-* / pending class in-place — no sibling needed.
function toggleHighlightOff(editor) {
  if (!editor) return false;
  if (editor.tagName === "TEXTAREA") return false; // nothing to toggle in plain text
  try { if (document.activeElement !== editor) editor.focus(); } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return false;
  const ancestor = findAncestorHighlightSpan(editor, range.startContainer);
  if (!ancestor) return false;

  const text = (ancestor.textContent || "");
  const stripped = text.replace(new RegExp(ZWSP, "g"), "");
  // Still-pending (empty / ZWSP-only) → strip classes in place.
  if (stripped.length === 0) {
    HL_CLASSES.forEach((c) => ancestor.classList.remove(c));
    ancestor.classList.remove(PENDING_INLINE_CLASS);
    if (ancestor.classList.length === 0) ancestor.removeAttribute("class");
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  // Committed highlight with real text → leave the span intact. Emerge
  // the caret to a ZWSP sibling just after it so new typing lands
  // outside the highlight. Cleanup walker will strip the ZWSP once the
  // user types a real character past it.
  const parent = ancestor.parentNode;
  if (!parent) return false;
  try {
    const exitAnchor = document.createTextNode(ZWSP);
    exitAnchor.__irExitAnchor = true;
    if (ancestor.nextSibling) parent.insertBefore(exitAnchor, ancestor.nextSibling);
    else parent.appendChild(exitAnchor);
    const nr = document.createRange();
    nr.setStart(exitAnchor, 1);
    nr.setEnd(exitAnchor, 1);
    sel.removeAllRanges();
    sel.addRange(nr);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch { return false; }
}

// --------------------------------------------------------------------------
// INLINE 3-zone row — content model + helpers.
//
// Shape (persisted verbatim through htmlSanitize):
//   <div class="ir-irow" data-ir="irow">
//     <span class="ir-icell ir-icell-left"   data-ir="cell" data-pos="left"   contenteditable="true">…</span>
//     <span class="ir-icell ir-icell-center" data-ir="cell" data-pos="center" contenteditable="true">…</span>
//     <span class="ir-icell ir-icell-right"  data-ir="cell" data-pos="right"  contenteditable="true">…</span>
//   </div>
//
// The three cells are independently editable regions on a single flex
// row. Each cell is a linear text flow → the frozen ZWSP mechanism
// (format-first-then-type for B/I/U/S/Highlighter/Aa) works inside a
// cell exactly as it works in a <p>.
// --------------------------------------------------------------------------
const INLINE_POSITIONS = ["left", "center", "right"];

function findInlineCell(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "SPAN" && walk.getAttribute && walk.getAttribute("data-ir") === "cell") return walk;
    walk = walk.parentElement;
  }
  return null;
}

function findInlineRow(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName === "DIV" && walk.getAttribute && walk.getAttribute("data-ir") === "irow") return walk;
    walk = walk.parentElement;
  }
  return null;
}

// Build a fresh, well-formed INLINE row with 3 empty cells. Each cell
// seeded with a ZWSP so the browser can place the caret inside; the
// ZWSP is harmless and is dropped the moment the user types.
function buildInlineRow(doc) {
  const row = doc.createElement("div");
  row.className = "ir-irow";
  row.setAttribute("data-ir", "irow");
  INLINE_POSITIONS.forEach((pos) => {
    const cell = doc.createElement("span");
    cell.className = `ir-icell ir-icell-${pos}`;
    cell.setAttribute("data-ir", "cell");
    cell.setAttribute("data-pos", pos);
    cell.setAttribute("contenteditable", "true");
    cell.appendChild(doc.createTextNode(ZWSP));
    row.appendChild(cell);
  });
  return row;
}

// Place the caret at the end of a given cell's text and focus it.
function moveCaretToCell(cell) {
  if (!cell) return false;
  try {
    cell.focus();
    const sel = window.getSelection && window.getSelection();
    if (!sel) return false;
    const nr = document.createRange();
    nr.selectNodeContents(cell);
    nr.collapse(false);
    sel.removeAllRanges();
    sel.addRange(nr);
    return true;
  } catch { return false; }
}

// Insert a fresh INLINE row at the block boundary nearest the caret
// and park the caret in the requested cell.
//   • If the caret's host block is EMPTY → replace it with the row.
//   • Otherwise → insert AFTER the host block (preserves prior text
//     verbatim; honours the "no retroactive movement" invariant).
// If the caret is already inside an INLINE row, returns null — the
// caller is expected to call moveCaretToCell(...) instead.
function insertInlineRow(editor, pos) {
  if (!editor) return null;
  if (editor.tagName === "TEXTAREA") return null;
  try { if (document.activeElement !== editor && !editor.contains(document.activeElement)) editor.focus(); } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  if (findInlineCell(editor, range.startContainer)) return null;

  const row = buildInlineRow(document);
  const blockAncestor = (() => {
    let w = range.startContainer;
    if (w && w.nodeType === Node.TEXT_NODE) w = w.parentElement;
    while (w && w !== editor) {
      if (w.tagName === "P" || w.tagName === "H1" || w.tagName === "H2" || w.tagName === "H3" || w.tagName === "LI") return w;
      w = w.parentElement;
    }
    return null;
  })();

  if (blockAncestor) {
    const visible = (blockAncestor.textContent || "").replace(new RegExp(ZWSP, "g"), "").replace(/\s/g, "");
    if (visible.length === 0 && blockAncestor.tagName === "P") {
      blockAncestor.parentNode.replaceChild(row, blockAncestor);
    } else {
      if (blockAncestor.nextSibling) blockAncestor.parentNode.insertBefore(row, blockAncestor.nextSibling);
      else blockAncestor.parentNode.appendChild(row);
    }
  } else {
    editor.appendChild(row);
  }

  const target = row.querySelector(`[data-pos="${pos}"]`);
  moveCaretToCell(target);
  editor.dispatchEvent(new Event("input", { bubbles: true }));
  return row;
}

// Exit the current row by inserting a fresh empty <p> AFTER the row
// and moving the caret into it. Called by the Enter interceptor.
function exitInlineRow(editor, row) {
  if (!editor || !row) return false;
  const p = document.createElement("p");
  p.appendChild(document.createElement("br"));
  if (row.nextSibling) row.parentNode.insertBefore(p, row.nextSibling);
  else row.parentNode.appendChild(p);
  try {
    const sel = window.getSelection && window.getSelection();
    const nr = document.createRange();
    nr.setStart(p, 0); nr.setEnd(p, 0);
    sel.removeAllRanges(); sel.addRange(nr);
    editor.focus();
  } catch {}
  editor.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}




// --------------------------------------------------------------------------
// Text Alignment — scoped block-level `text-align` via utility classes
// `ir-align-left|center|right` applied to the NEAREST block ancestor
// (P/H1/H2/H3/LI). This design keeps inline formatting (B/I/U/S/
// Highlight/Aa Color/Aa Size) 100% untouched because those live on
// descendant inline spans. Headings remain their native tag; we only
// toggle the alignment class on the block itself. Mirrors the data
// model used by the frozen Text Tools baseline — no DOM re-wrapping,
// no block re-promotion, no regression surface.
//
// Toggle semantics: tapping the SAME alignment currently in effect
// removes the class entirely (block reverts to the editor's default
// alignment — i.e. left in LTR). Tapping a DIFFERENT alignment swaps
// the class. This matches the user-approved "A" option.
// --------------------------------------------------------------------------
const ALIGN_CLASSES = ["ir-align-left", "ir-align-center", "ir-align-right"];
const BLOCK_TAGS_FOR_ALIGN = new Set(["P", "H1", "H2", "H3", "LI"]);

// Used by the pre-emptive empty-inline-wrapper strip on Enter (see the
// BLOCK Return-exit path below). Scoped intentionally wide for the
// BLOCK boundary (we halt the upward walk at any real block, including
// list containers and preformatted blocks); the INLINE_WRAPPER set is
// the exact tag list that may legitimately host a format-first ZWSP or
// end up empty after a Backspace / Chrome-insertParagraph clone. The
// strip only removes elements whose textContent (after ZWSP removal)
// is empty, so non-empty formatted runs are never affected.
const BLOCK_SET_FOR_INLINE_STRIP = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6",
  "LI", "UL", "OL",
  "DIV", "BLOCKQUOTE", "PRE",
  "TD", "TH", "TR", "TABLE",
]);
const INLINE_WRAPPER_SET_FOR_STRIP = new Set([
  "SPAN", "B", "STRONG", "I", "EM", "U",
  "S", "STRIKE", "DEL",
  "A", "MARK", "CODE", "SUB", "SUP", "FONT",
]);

function findAlignBlockAncestor(editor, node) {
  let walk = node;
  if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
  while (walk && walk !== editor) {
    if (walk.tagName && BLOCK_TAGS_FOR_ALIGN.has(walk.tagName)) return walk;
    walk = walk.parentElement;
  }
  return null;
}

function readActiveAlign(editor) {
  if (!editor) return null;
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  const block = findAlignBlockAncestor(editor, range.startContainer);
  if (!block || !block.classList) return null;
  for (let i = 0; i < ALIGN_CLASSES.length; i++) {
    if (block.classList.contains(ALIGN_CLASSES[i])) return ALIGN_CLASSES[i];
  }
  return null;
}

// Collect every block ancestor touched by the current selection — single
// block for a collapsed caret; every spanned block for a multi-block
// range. Alignment is applied/toggled on each. Keeps multi-paragraph
// selections working the same as any native WYSIWYG.
function collectAlignBlocksInSelection(editor, range) {
  const blocks = [];
  const seen = new Set();
  const add = (b) => { if (b && !seen.has(b)) { seen.add(b); blocks.push(b); } };
  if (range.collapsed) {
    add(findAlignBlockAncestor(editor, range.startContainer));
    return blocks;
  }
  // Walk every block descendant inside the common ancestor and keep
  // those that intersect the range. Cheap for note-sized documents.
  const container = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentElement;
  if (!container) return blocks;
  // Start/end's own block always counts.
  add(findAlignBlockAncestor(editor, range.startContainer));
  add(findAlignBlockAncestor(editor, range.endContainer));
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, null);
  let el;
  while ((el = walker.nextNode())) {
    if (!BLOCK_TAGS_FOR_ALIGN.has(el.tagName)) continue;
    if (!editor.contains(el)) continue;
    try { if (range.intersectsNode(el)) add(el); } catch {}
  }
  return blocks;
}

// Apply alignment. `className` must be one of ALIGN_CLASSES or null to
// clear. Toggle rule: if the block already has `className`, remove it
// (revert to default). Otherwise swap in the new class.
//
// Caret-vs-selection semantics (per user's spec):
//   • NON-COLLAPSED selection → align every block the selection spans
//     (toggle-off when the same alignment is already applied to all).
//   • COLLAPSED caret → align the block that CONTAINS the caret
//     (toggle-off on same alignment). This matches native WYSIWYG
//     behaviour (Word / Google Docs). The alignment tool NEVER creates
//     a new block on its own — Return/Enter is the only way to create
//     a new paragraph. Earlier paragraphs are never touched because we
//     only ever act on the single block hosting the caret.
function applyAlign(editor, className) {
  if (!editor) return false;
  if (editor.tagName === "TEXTAREA") {
    // Promote to HTML first so there is a real block to align on.
    const mode = promoteTextareaWithAppearance(editor, null);
    if (mode) focusHtmlEditorSoon(mode);
    // Re-resolve editor after promotion.
    const ed2 = findActiveEditor();
    if (!ed2 || ed2.tagName === "TEXTAREA") return !!mode;
    return applyAlign(ed2, className);
  }
  try { if (document.activeElement !== editor) editor.focus(); } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return false;

  // Caret-vs-selection semantics (per user spec — restored flow):
  //
  //   • NON-COLLAPSED selection → align every block the selection spans
  //     (toggle-off when the same class is already there).
  //
  //   • COLLAPSED caret in an EMPTY block → align that block in place.
  //     Typing starts aligned. Toggle rule applies.
  //
  //   • COLLAPSED caret in a NON-EMPTY block → DO NOT retroactively
  //     move the existing text. Instead, insert a FRESH <p> just AFTER
  //     the current block, stamp the requested alignment on it, and
  //     move the caret into the new block. New text the user types
  //     next lands aligned on the new line below. Previously-typed
  //     text keeps its own alignment untouched. This is the behaviour
  //     the user confirmed working this morning.
  //
  // Return (Enter) inside an aligned block still "exits" to a plain
  // LEFT paragraph via the keydown interceptor — orthogonal to this
  // function and intentionally kept.
  if (!range.collapsed) {
    const blocks = collectAlignBlocksInSelection(editor, range);
    if (blocks.length === 0) return false;
    blocks.forEach((block) => {
      const already = className && block.classList.contains(className);
      ALIGN_CLASSES.forEach((c) => block.classList.remove(c));
      if (className && !already) block.classList.add(className);
      if (block.classList.length === 0) block.removeAttribute("class");
    });
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  const block = findAlignBlockAncestor(editor, range.startContainer);
  if (!block) return false;

  // "Empty" means no visible characters: strip ZWSPs + whitespace.
  const visibleText = (block.textContent || "").replace(/\u200B/g, "").replace(/\s/g, "");
  const isEmpty = visibleText.length === 0;

  if (isEmpty) {
    // Align the empty block in place. Toggle rule: same class twice
    // removes it (block reverts to editor-default alignment).
    const already = className && block.classList.contains(className);
    ALIGN_CLASSES.forEach((c) => block.classList.remove(c));
    if (className && !already) block.classList.add(className);
    if (block.classList.length === 0) block.removeAttribute("class");
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  // Non-empty block → insert a fresh aligned <p> AFTER the current
  // block. Caret moves into the new <p>. Previous text is never moved.
  //
  // Toggle edge-case: if the current block already carries exactly the
  // requested class, treat it as "give me a plain line below"
  // (prevents a dead no-op; still never mutates the existing block).
  const sameAsCurrent = className && block.classList.contains(className);
  const newBlock = document.createElement("p");
  if (className && !sameAsCurrent) newBlock.classList.add(className);
  // Empty <p> needs a <br> so it has caret-visible height in every
  // engine. The <br> is stripped on first real keystroke by the
  // browser's native input handling — same convention we use for the
  // Return-exit path.
  newBlock.appendChild(document.createElement("br"));
  if (block.nextSibling) block.parentNode.insertBefore(newBlock, block.nextSibling);
  else block.parentNode.appendChild(newBlock);
  try {
    const nr = document.createRange();
    nr.setStart(newBlock, 0);
    nr.setEnd(newBlock, 0);
    sel.removeAllRanges();
    sel.addRange(nr);
  } catch { /* noop */ }
  editor.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}




//
// Also strips exit-anchor ZWSPs (plain text nodes stamped with
// `__irExitAnchor`) once the user has typed past them. These are the
// tiny caret-anchor nodes we inject when toggling OFF a committed
// inline format — the ZWSP keeps the caret visible outside the tag;
// once a real char lands next to it we no longer need the padding.
function cleanupPendingInlineMarkers(editor) {
  if (!editor) return;
  try {
    const markers = editor.querySelectorAll("." + PENDING_INLINE_CLASS);
    markers.forEach((m) => {
      const text = m.textContent || "";
      if (text === ZWSP || text.length === 0) return;
      let n = m.firstChild;
      while (n) {
        if (n.nodeType === Node.TEXT_NODE && n.data && n.data.indexOf(ZWSP) !== -1) {
          n.data = n.data.replace(new RegExp(ZWSP, "g"), "");
          if (!n.data) {
            const nxt = n.nextSibling;
            m.removeChild(n);
            n = nxt;
            continue;
          }
        }
        n = n.nextSibling;
      }
      m.classList.remove(PENDING_INLINE_CLASS);
      if (m.classList.length === 0) m.removeAttribute("class");
    });
    // Exit-anchor ZWSP cleanup. We walk every text node in the editor
    // (cheap — editors are tiny), check for the JS-side flag, and if
    // the node now contains > 1 character the ZWSP has done its job
    // and can go.
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null);
    let n;
    const toClean = [];
    while ((n = walker.nextNode())) {
      if (n.__irExitAnchor && (n.data || "").length > 1) toClean.push(n);
    }
    toClean.forEach((n) => {
      n.data = n.data.replace(new RegExp(ZWSP, "g"), "");
      n.__irExitAnchor = false;
    });
  } catch { /* noop */ }
}


function wrapSelectionWithTag(editor, tagName) {
  if (!editor) return false;
  // Fix E.1 — see runExecCommand. Only focus if focus has drifted away;
  // calling .focus() on an already-focused contentEditable on Android
  // Chrome collapses the selection, which turns this helper into a
  // silent no-op (range.collapsed → early return) exactly matching the
  // "Strike needs multiple taps" symptom reported on-device.
  try {
    if (document.activeElement !== editor) editor.focus();
  } catch {}
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (range.collapsed) return false;
  // Guard: ensure the selection lives inside the editor we were handed
  // (avoid formatting text that has scrolled or drifted outside).
  if (!editor.contains(range.commonAncestorContainer)) return false;
  try {
    const wrapper = document.createElement(tagName);
    // extractContents + appendChild works even when the selection
    // crosses element boundaries (surroundContents throws in that case).
    wrapper.appendChild(range.extractContents());
    range.insertNode(wrapper);
    // Restore selection to the wrapped content so a follow-up format
    // click continues to act on the same text.
    sel.removeAllRanges();
    const nr = document.createRange();
    nr.selectNodeContents(wrapper);
    sel.addRange(nr);
    // Notify React that the DOM changed so NoteModal's handleHtmlInput
    // syncs the new HTML into `content` state (and therefore storage).
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch {
    return false;
  }
}

// Fix F — strike toggle. When the caret / selection already lives
// inside an `<s>` (or `<strike>` / `<del>`) ancestor, re-tapping Strike
// should REMOVE the formatting, matching how Bold/Italic/Underline
// behave via native execCommand. The existing `wrapSelectionWithTag`
// unconditionally wraps, producing `<s><s>text</s></s>` on the second
// tap — which the user caught during Android testing.
//
// Strategy: find the nearest strike ancestor that is wholly contained
// in the editor; unwrap it by replacing the <s> with its own children,
// then restore the selection to span those children. The caret-is-
// inside-formatted-text active-state detector (`refreshActive`) already
// walks the same tag names, so the button correctly lights up / clears.
//
// This helper is strike-only on purpose — Bold/Italic/Underline/H1-H3
// paths remain exactly as they were, so this fix cannot regress any
// confirmed-working command.
function unwrapStrikeAtSelection(editor) {
  if (!editor) return false;
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return false;
  // Walk up from the selection's start container to find the nearest
  // strike ancestor still contained within the editor.
  let node = range.startContainer;
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  let strike = null;
  while (node && node !== editor) {
    const t = node.tagName;
    if (t === "S" || t === "STRIKE" || t === "DEL") { strike = node; break; }
    node = node.parentElement;
  }
  if (!strike) return false;
  try {
    const parent = strike.parentNode;
    if (!parent) return false;
    // Capture the first/last children before unwrap so we can restore
    // the selection to span exactly the un-struck text — not a tag.
    const firstChild = strike.firstChild;
    const lastChild = strike.lastChild;
    // Move each child of <s> out, in order, into the parent at the
    // position <s> currently occupies.
    while (strike.firstChild) parent.insertBefore(strike.firstChild, strike);
    parent.removeChild(strike);
    if (firstChild && lastChild) {
      const nr = document.createRange();
      nr.setStartBefore(firstChild);
      nr.setEndAfter(lastChild);
      sel.removeAllRanges();
      sel.addRange(nr);
    }
    editor.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  } catch {
    return false;
  }
}

// Map of format key → inline tag used when promoting a plain textarea
// into HTML mode. Kept in sync with the sanitiser allowlist in
// utils/htmlSanitize.js so the promoted markup round-trips through
// storage cleanly.
const INLINE_TAG = { bold: "strong", italic: "em", underline: "u", strike: "s" };
const BLOCK_TAG = { h1: "h1", h2: "h2", h3: "h3" };

// Split plaintext-with-markers into HTML paragraphs while preserving the
// markers verbatim, then swap them back to real tags. This gives us
// selection-preserving wrapping without any Markdown intermediate step.
const MARK_OPEN = "\u0001IR_OPEN\u0001";
const MARK_CLOSE = "\u0001IR_CLOSE\u0001";

// Promotion marker — a transient class we stamp onto the newly-wrapped
// tag inside the HTML we hand to React. After the contentEditable mounts
// and React commits the new innerHTML, we locate `.ir-caret-target`,
// restore selection to it, then strip the class. The class is on the
// DOMPurify ALLOWED_ATTR list so it survives sanitize.
const CARET_TARGET_CLASS = "ir-caret-target";
// Zero-width space — used as a caret anchor inside otherwise-empty
// promoted elements (format-first-then-type flow). Browsers keep inline
// elements around when they contain at least one character, giving us a
// stable Range target. The ZWSP is deleted the moment we finish
// positioning the caret so the user never types past an invisible char.
const ZWSP = "\u200B";

function promoteTextareaWithInlineFormat(editor, format) {
  const tag = INLINE_TAG[format];
  if (!tag) return false;
  const start = editor.selectionStart ?? 0;
  const end = editor.selectionEnd ?? 0;
  const value = editor.value || "";

  if (start !== end) {
    // Existing path (Fix B): user has a selection — wrap it in the
    // inline tag and tell focusHtmlEditorSoon to re-select the inner
    // contents so chaining B → I → U keeps working.
    const before = value.slice(0, start);
    const middle = value.slice(start, end);
    const after = value.slice(end);
    const marked = before + MARK_OPEN + middle + MARK_CLOSE + after;
    let html = plainTextToHtml(marked);
    html = html
      .split(MARK_OPEN).join(`<${tag} class="${CARET_TARGET_CLASS}">`)
      .split(MARK_CLOSE).join(`</${tag}>`);
    reactSetValue(editor, html);
    return "range";
  }

  // Fix D — format-first-then-type (no selection). Promote the textarea
  // while preserving any existing text and the exact caret position,
  // then signal the pending inline format. focusHtmlEditorSoon will
  // land the caret at the promoted spot and toggle execCommand(<format>)
  // so the next keystroke inherits bold/italic/underline — matching
  // the way every mainstream rich-text editor behaves.
  //
  // Caret anchor strategy: we insert `<b class="ir-caret-target">\u200B</b>`
  // at the caret position. `<b>` + `class` + a ZWSP survive the
  // sanitiser round-trip, and the ZWSP keeps the empty inline alive
  // through contentEditable mount (browsers collapse truly-empty inline
  // elements). The marker is torn down in focusHtmlEditorSoon.
  const before = value.slice(0, start);
  const after = value.slice(start);
  const marked = before + MARK_OPEN + ZWSP + MARK_CLOSE + after;
  let html = plainTextToHtml(marked);
  if (!html) {
    // Empty textarea → emit a minimal paragraph that owns the marker.
    html = `<p>${MARK_OPEN}${ZWSP}${MARK_CLOSE}</p>`;
  }
  html = html
    .split(MARK_OPEN).join(`<b class="${CARET_TARGET_CLASS}">`)
    .split(MARK_CLOSE).join(`</b>`);
  reactSetValue(editor, html);
  return `pending-inline:${format}`;
}

// Promote a textarea into contentEditable with a pending `<span>` marker
// that carries BOTH the caret-target class (so focusHtmlEditorSoon can
// find it) AND the appearance class (ir-size-* or ir-color-*). After
// mount, cleanupCaretMarker strips ir-caret-target only, leaving the
// span as a committed `<span class="ir-pending-inline ir-size-lg">` with
// the ZWSP + caret inside. User's next keystroke extends the span, same
// mechanism as Bold/Italic/Underline/Strike.
function promoteTextareaWithAppearance(editor, className) {
  if (!editor || editor.tagName !== "TEXTAREA") return false;
  const value = editor.value || "";
  const caret = editor.selectionStart ?? 0;
  const before = value.slice(0, caret);
  const after = value.slice(caret);
  const marked = before + MARK_OPEN + ZWSP + MARK_CLOSE + after;
  let html = plainTextToHtml(marked);
  if (!html) {
    html = `<p>${MARK_OPEN}${ZWSP}${MARK_CLOSE}</p>`;
  }
  const classAttr = [CARET_TARGET_CLASS, PENDING_INLINE_CLASS, className].filter(Boolean).join(" ");
  html = html
    .split(MARK_OPEN).join(`<span class="${classAttr}">`)
    .split(MARK_CLOSE).join(`</span>`);
  reactSetValue(editor, html);
  return `pending-inline:appearance`;
}

function promoteTextareaWithBlockFormat(editor, format) {
  const tag = BLOCK_TAG[format];
  if (!tag) return false;
  const value = editor.value || "";
  const caret = editor.selectionStart ?? 0;
  const lineStart = value.lastIndexOf("\n", caret - 1) + 1;
  const nextNl = value.indexOf("\n", caret);
  const lineEnd = nextNl === -1 ? value.length : nextNl;
  const before = value.slice(0, lineStart);
  const line = value.slice(lineStart, lineEnd);
  const after = value.slice(lineEnd);

  if (line.trim()) {
    // Existing path (Fix B): wrap the non-empty line in <h1>|<h2>|<h3>
    // and park the caret at the end of the promoted block so H1 → H2
    // → H3 re-levelling works.
    const marked = before + MARK_OPEN + line + MARK_CLOSE + after;
    let html = plainTextToHtml(marked);
    html = html
      .split(MARK_OPEN).join(`<${tag} class="${CARET_TARGET_CLASS}">`)
      .split(MARK_CLOSE).join(`</${tag}>`);
    reactSetValue(editor, html);
    return "collapsed-end";
  }

  // Fix D — format-first-then-type on an empty line (or empty note).
  // Promote preserving the surrounding text and emit an empty block of
  // the chosen tag, with a ZWSP marker so the Range has somewhere to
  // anchor. On mount, focusHtmlEditorSoon strips the ZWSP, drops a <br>
  // into the empty block so the caret has a visible home, and leaves
  // the user ready to type directly into the heading.
  const marked = before + MARK_OPEN + ZWSP + MARK_CLOSE + after;
  let html = plainTextToHtml(marked);
  if (!html) {
    html = `<${tag} class="${CARET_TARGET_CLASS}">${ZWSP}</${tag}>`;
  } else {
    html = html
      .split(MARK_OPEN).join(`<${tag} class="${CARET_TARGET_CLASS}">`)
      .split(MARK_CLOSE).join(`</${tag}>`);
  }
  reactSetValue(editor, html);
  return "block-empty";
}

// Refocus the freshly-mounted contentEditable after a textarea → HTML
// promotion AND restore selection inside the `.ir-caret-target` wrapper
// so subsequent toolbar operations find a valid Range to act on. Works
// for either host (NoteModal's `note-content-input-html` or
// FullScreenNote's `fullscreen-content-input-format`).
// `mode` is the return value of a promote* helper:
//   • "range"                    → select the full contents (inline w/ selection)
//   • "collapsed-end"            → caret at end (block on non-empty line)
//   • "pending-inline:<format>"  → caret at marker, strip marker, exec toggle
//                                   (Fix D — inline format-first-then-type)
//   • "block-empty"              → caret inside empty block, strip ZWSP + ensure <br>
//                                   (Fix D — block format-first-then-type)
//   • true | falsy               → focus only, no selection restore (legacy)
function focusHtmlEditorSoon(mode = true) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const el = findActiveHtmlEditor();
      if (!el || typeof el.focus !== "function") return;
      el.focus();
      if (mode === true || mode === false || !mode) return;

      const target = el.querySelector(`.${CARET_TARGET_CLASS}`);
      if (!target) return;

      const sel = window.getSelection && window.getSelection();
      if (!sel) return;

      try {
        if (mode === "range") {
          const range = document.createRange();
          range.selectNodeContents(target);
          sel.removeAllRanges(); sel.addRange(range);
          cleanupCaretMarker(target);
        } else if (mode === "collapsed-end") {
          const range = document.createRange();
          let node = target;
          while (node && node.lastChild) node = node.lastChild;
          if (node && node.nodeType === Node.TEXT_NODE) {
            range.setStart(node, node.data.length);
            range.setEnd(node, node.data.length);
          } else {
            range.selectNodeContents(target); range.collapse(false);
          }
          sel.removeAllRanges(); sel.addRange(range);
          cleanupCaretMarker(target);
        } else if (typeof mode === "string" && mode.startsWith("pending-inline:")) {
          // Mirror H1/H2/H3's "block-empty" strategy EXACTLY: keep the
          // promoted inline tag intact, place the caret inside it, and
          // let the user type directly into the tag. The ZWSP stays as
          // an invisible content anchor so Chrome doesn't collapse the
          // empty inline on focus. We do NOT unwrap the marker, do NOT
          // fire execCommand (unreliable on Capacitor Android), and do
          // NOT mutate text nodes mid-type (that caused "Oldb").
          const range = document.createRange();
          // Caret AFTER the ZWSP so the first typed char lands inside
          // the tag, after the ZWSP. Result: `<b>\u200BBold</b>` which
          // renders as "Bold" (ZWSP is zero-width, user never sees it)
          // with the tag's formatting applied.
          const firstText = (function findText(n) {
            if (!n) return null;
            if (n.nodeType === Node.TEXT_NODE) return n;
            for (const c of (n.childNodes || [])) {
              const r = findText(c); if (r) return r;
            }
            return null;
          })(target);
          if (firstText) {
            range.setStart(firstText, firstText.data.length);
            range.setEnd(firstText, firstText.data.length);
          } else {
            range.selectNodeContents(target);
            range.collapse(false);
          }
          sel.removeAllRanges(); sel.addRange(range);
          cleanupCaretMarker(target);
        } else if (mode === "block-empty") {
          // Caret inside the empty block. Strip the ZWSP so the first
          // keystroke doesn't trail an invisible char, and plant a <br>
          // so the empty block holds a visible line height on all
          // browsers.
          let node = target.firstChild;
          const range = document.createRange();
          if (node && node.nodeType === Node.TEXT_NODE) {
            node.data = "";
          }
          // Ensure a <br> placeholder — this is what Chrome itself
          // writes into empty <p> blocks during contentEditable init.
          if (!target.querySelector("br") && !target.textContent) {
            target.appendChild(document.createElement("br"));
          }
          range.setStart(target, 0); range.setEnd(target, 0);
          sel.removeAllRanges(); sel.addRange(range);
          cleanupCaretMarker(target);
        }
      } catch { /* fail silent — focus alone is still a usable fallback */ }

      el.dispatchEvent(new Event("input", { bubbles: true }));
    });
  });
}

// Remove the `.ir-caret-target` sentinel class once we've used it to
// restore selection. Leaves the element itself in place.
function cleanupCaretMarker(target) {
  try {
    target.classList.remove(CARET_TARGET_CLASS);
    if (target.classList.length === 0) target.removeAttribute("class");
  } catch { /* noop */ }
}

// Dissolve the inline `<b class="ir-caret-target">ZWSP</b>` marker used
// by the pending-inline flow. The caret ends up at the position the
// marker occupied inside its parent block, with no stray <b> and no
// invisible chars left over. If anything in this dissolve throws, we
// fall back to simply positioning the caret inside the marker and
// stripping the class — the user still gets a caret in the right block.
function dissolveInlineCaretMarker(editor, target, sel) {
  const parent = target.parentNode;
  if (!parent) { cleanupCaretMarker(target); return; }
  try {
    // Place caret immediately before the marker in its parent so
    // subsequent typing goes into the parent's text flow.
    const range = document.createRange();
    const indexOfMarker = Array.prototype.indexOf.call(parent.childNodes, target);
    // Pull marker's children (should be a single ZWSP text node) out
    // and discard — we don't want them in the parent.
    while (target.firstChild) target.removeChild(target.firstChild);
    parent.removeChild(target);
    // If the parent is now empty (common when promoting an empty
    // textarea), seed it with a <br> so Chrome maintains a visible
    // caret line and formatBlock has something to re-wrap later.
    if (!parent.firstChild) {
      parent.appendChild(document.createElement("br"));
    }
    // Position caret at the index the marker used to occupy. clamp to
    // parent's current childNodes.length since we just mutated it.
    const safeIdx = Math.min(indexOfMarker, parent.childNodes.length);
    range.setStart(parent, safeIdx);
    range.setEnd(parent, safeIdx);
    sel.removeAllRanges(); sel.addRange(range);
    // Refocus so the pending execCommand toggle lands on the right
    // selection.
    try { editor.focus(); } catch { /* noop */ }
  } catch {
    cleanupCaretMarker(target);
  }
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------
// These match the rendered outer-box size (button height + 4px padding +
// 1px border top/bottom). They only matter for the "sit above the keyboard"
// Y calculation on mobile; getting them off by a few px is what determines
// whether the toolbar overshoots into the keyboard area.
const HORIZONTAL_HEIGHT = 50;
const VERTICAL_WIDTH = 50;

export default function FloatingRichTextToolbar({ isDark = true, isOpen = true }) {
  const [pos, setPos] = useState(null);            // { x, y } in viewport coords
  const [orientation, setOrientation] = useState("horizontal");
  const [pickerOpen, setPickerOpen] = useState(null); // null | "forms" | "hierarchy"
  // Aa (Text Appearance) dropdown. Floating overlay, portaled to body.
  // `aaPanel.anchor` is the Aa button's bounding rect at open time, used
  // to position the panel. Opening/closing does NOT change the toolbar
  // layout or the editor layout — the panel is a pure overlay.
  const [aaPanel, setAaPanel] = useState(null); // null | { anchor: DOMRect }
  // Highlighter — floating color palette for text-background highlighting.
  // Same overlay / portal / touch-isolation pattern as the Aa panel.
  const [hlPanel, setHlPanel] = useState(null);
  // Alignment — small floating palette (L/C/R) mirroring the Highlighter
  // panel architecture. Positioned ABOVE the toolbar (preferred) with a
  // fall-through to below when there is not enough vertical room.
  const [alignPanel, setAlignPanel] = useState(null);
  const dragStateRef = useRef({ dragging: false, offX: 0, offY: 0 });
  const scrollerRef = useRef(null);

  // Custom undo/redo stack (spec §9/§10). Scoped per toolbar lifetime —
  // which is per modal-open — so each note gets a fresh history.
  const historyRef = useRef(null);
  const historyEditorRef = useRef(null);
  if (!historyRef.current) historyRef.current = new HistoryStack(200);

  // ------------------------------------------------------------------------
  // History manager binding + interactive checklist delegation.
  // Runs as long as the toolbar is open. Polls document for the HTML
  // editor (which may not exist initially if the note is in textarea
  // mode) and primes/rebinds whenever it swaps in or out.
  // ------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const history = historyRef.current;
    let currentEl = null;
    let onInput = null;

    const attach = (el) => {
      if (currentEl === el) return;
      detach();
      currentEl = el;
      historyEditorRef.current = el;
      if (!el) return;
      history.resetTo(el.innerHTML, serializeSelection(el));
      onInput = () => history.snapshotDebounced(el);
      el.addEventListener("input", onInput);
    };
    const detach = () => {
      if (currentEl && onInput) {
        currentEl.removeEventListener("input", onInput);
      }
      currentEl = null;
      onInput = null;
      historyEditorRef.current = null;
    };

    // Poll for the HTML editor mount/unmount every 250ms. Cheap; the
    // alternative is a MutationObserver which has subtler lifecycle.
    const poll = () => {
      const el = findActiveHtmlEditor();
      if (el !== currentEl) attach(el);
    };
    poll();
    const timer = setInterval(poll, 250);

    // Interactive checklist click toggle. Delegated at document level so
    // we only need one listener regardless of how many checklist UL's
    // the user has in the note.
    const onDocClick = (e) => {
      const li = e.target && e.target.closest && e.target.closest("ul.ir-checklist > li");
      if (!li) return;
      // Only toggle when the user clicks the box area (left of the text).
      // Approx: anywhere within 1.75rem (~28px) of the LI's left edge.
      const rect = li.getBoundingClientRect();
      if (e.clientX - rect.left > 28) return;
      const next = li.getAttribute("data-ir-check") === "1" ? "0" : "1";
      li.setAttribute("data-ir-check", next);
      // Notify React so the host editor picks up the DOM change and
      // persists. Works for either NoteModal or FullScreenNote.
      const editor = li.closest(
        '[data-testid="note-content-input-html"],[data-testid="fullscreen-content-input-format"]'
      );
      if (editor) {
        editor.dispatchEvent(new Event("input", { bubbles: true }));
        if (historyRef.current) historyRef.current.snapshotImmediate(editor);
      }
      e.preventDefault();
    };
    document.addEventListener("click", onDocClick);

    return () => {
      clearInterval(timer);
      detach();
      document.removeEventListener("click", onDocClick);
    };
  }, [isOpen]);


  // ------------------------------------------------------------------------
  // Mobile-only visibility contract (see Module 1 correction):
  //   1. Hidden until the Expanded Text Editor is focused AND the on-screen
  //      keyboard is up.
  //   2. Y is snapped to just-above-the-keyboard on every visualViewport
  //      change so it visually travels with the keyboard.
  //   3. Hidden again when the editor loses focus or the keyboard closes.
  //   4. Desktop behaviour is unchanged (always shown when open).
  // Scope: ALL known Expanded Text Editor targets — NoteModal
  // (`note-content-input*`) and FullScreenNote
  // (`fullscreen-content-input*`). Other Tile Packs / editors are untouched.
  // ------------------------------------------------------------------------
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    try { return window.matchMedia("(pointer: coarse)").matches; } catch { return false; }
  }, []);
  const [editorFocused, setEditorFocused] = useState(false);
  const [keyboardUp, setKeyboardUp] = useState(false);

  // ------------------------------------------------------------------------
  // Fix C — active-state detection for toolbar highlighting (Edit Text).
  //
  // Logic ported from `FormatFloatingToolbar.refreshActive` (the inner
  // Expanded Text toolbar, which the user has already validated works
  // correctly). We walk the ancestor chain from the caret / selection
  // start up to the nearest editable root and look for matching tag
  // names. We deliberately DO NOT use `document.queryCommandState`
  // because the Module 1 toolbar uses direct DOM wrappers (<s> for
  // strike, class-tagged <ul.ir-checklist> for checklists) that
  // execCommand doesn't know about — queryCommandState would under-
  // report Strikethrough and over-report Underline on Capacitor
  // WebViews. The ancestor walk is the single source of truth.
  //
  // When the active editor is a <textarea> (plain-text mode in
  // NoteModal before the first promotion, or `text`/`html` mode in
  // ExpandedTextEditor), there is no inline markup to detect, so every
  // flag stays false. This is correct behaviour — pressing Bold in a
  // textarea goes through the promotion path which switches modes.
  //
  // Scope note: this adds visual-only state. It cannot regress
  // Expanded Text because:
  //   • The inner FormatFloatingToolbar continues to run its own
  //     highlight logic completely independently.
  //   • We never mutate the DOM here — only read it.
  // ------------------------------------------------------------------------
  const [active, setActive] = useState({
    bold: false, italic: false, underline: false, strike: false,
    link: false,
    block: null, // "P" | "H1" | "H2" | "H3" | null
    // Aa (Text Appearance) — independent of the inline-format and block
    // axes. True iff the caret sits inside any `<span>` carrying an
    // `ir-size-*` OR `ir-color-*` class. Lights the Aa toolbar button
    // the same way `bold` lights the Bold button.
    aa: false,
    // Highlight (text-background) — true iff caret is inside a span
    // carrying any `ir-hl-*` class. Independent of Aa / block / inline
    // formats; stacks with all of them.
    hl: false,
    // Alignment — the active ir-align-* class on the nearest block
    // ancestor (P/H1/H2/H3/LI), or null when the block has none
    // (editor-default alignment). Used by `isToolActive("align")` to
    // light up the Alignment toolbar button, and by the AlignPanel to
    // show the currently-selected swatch.
    align: null,
    // List — "ul" | "ol" | "checklist" | null. Lights the matching
    // Bulleted / Numbered / Checklist tool button.
    list: null,
  });

  const refreshActive = useCallback(() => {
    const editor = findActiveEditor();
    if (!editor) {
      setActive((prev) => (prev.bold || prev.italic || prev.underline || prev.strike || prev.link || prev.block || prev.aa || prev.hl || prev.align || prev.list)
        ? { bold: false, italic: false, underline: false, strike: false, link: false, block: null, aa: false, hl: false, align: null, list: null }
        : prev);
      return;
    }
    // Textarea has no inline markup to detect.
    if (editor.tagName === "TEXTAREA") {
      setActive((prev) => (prev.bold || prev.italic || prev.underline || prev.strike || prev.link || prev.block || prev.aa || prev.hl || prev.align || prev.list)
        ? { bold: false, italic: false, underline: false, strike: false, link: false, block: null, aa: false, hl: false, align: null, list: null }
        : prev);
      return;
    }
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.startContainer)) return;
    let bold = false, italic = false, underline = false, strike = false, link = false;
    let block = null;
    let aa = false;
    let hl = false;
    let align = null;
    // list — "ul" (plain bulleted), "ol" (numbered), or "checklist"
    // (UL with the `ir-checklist` class). Mutually exclusive by design;
    // the UL/OL ancestor terminates the walk the same way P/H1/H2/H3
    // does. Enables active-state highlight on the Bulleted / Numbered
    // / Checklist toolbar buttons.
    let list = null;
    // Document-level size on the editor root also counts as "Aa active".
    try {
      if (editor.classList) {
        for (let i = 0; i < AA_SIZE_CLASSES.length; i++) {
          if (editor.classList.contains(AA_SIZE_CLASSES[i])) { aa = true; break; }
        }
      }
    } catch {}
    let node = range.startContainer;
    if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    // Track the deepest/innermost pending-inline-hl ancestor so we can
    // keep its icon lit while it's still "primed", but we only light
    // the Highlighter if the span is NOT empty — otherwise a stale
    // empty marker (user tapped HL, moved caret away, came back) kept
    // the icon lit even though no new text was highlighted.
    let pendingHlAncestor = null;
    // Also track the innermost COMMITTED (non-pending) ir-hl-* span
    // the caret is inside. The orphan sweep below must spare this
    // span even if it happens to be empty right now — the user may
    // be mid-edit inside a legitimately highlighted run.
    let committedHlAncestor = null;
    while (node && node !== editor) {
      const tag = node.tagName;
      if (tag === "B" || tag === "STRONG") bold = true;
      else if (tag === "I" || tag === "EM") italic = true;
      else if (tag === "U") underline = true;
      else if (tag === "S" || tag === "STRIKE" || tag === "DEL") strike = true;
      else if (tag === "A") link = true;
      else if (tag === "SPAN" && node.classList) {
        const isPendingInline = node.classList.contains(PENDING_INLINE_CLASS);
        const spanHasHl = (() => {
          for (const c of node.classList) { if (c.indexOf("ir-hl-") === 0) return true; }
          return false;
        })();
        // Aa flag — stays true as long as a size/color class is above
        // the caret, regardless of pending/empty state (unchanged).
        node.classList.forEach((c) => {
          if (c.indexOf("ir-size-") === 0 || c.indexOf("ir-color-") === 0) aa = true;
        });
        // Highlighter flag — committed highlight spans always light.
        // Pending-inline-hl markers only light when they contain real
        // user text (not just a ZWSP). Prevents the stale "icon stays
        // lit after priming + walking away" glitch.
        if (spanHasHl) {
          if (!isPendingInline) {
            hl = true;
            if (!committedHlAncestor) committedHlAncestor = node;
          } else {
            pendingHlAncestor = node;
            const txt = (node.textContent || "").replace(new RegExp(ZWSP, "g"), "");
            if (txt.length > 0) hl = true;
          }
        }
      }
      // List detection — first UL/OL ancestor wins. `ir-checklist` on
      // UL means the "Checklist" tool button lights (not the plain
      // Bulleted icon). Mutually exclusive with ol.
      if (list === null && (tag === "UL" || tag === "OL")) {
        if (tag === "OL") list = "ol";
        else if (node.classList && node.classList.contains("ir-checklist")) list = "checklist";
        else list = "ul";
      }
      if (!block && (tag === "P" || tag === "H1" || tag === "H2" || tag === "H3")) {
        // Fix G — stop the ancestor walk at block boundaries. Inline
        // formats CANNOT validly wrap block elements (P/H1/H2/H3), so
        // any <b>/<strong>/<i>/etc. that still exists above a block
        // boundary is either stale (left over from a prior React
        // re-render) or Capacitor-WebView-emitted invalid markup. Not
        // breaking out here was the root of the Android "Bold lights
        // up inside H1" bug: a <strong> ancestor of the whole editor
        // (from an earlier promote-with-inline operation) was being
        // detected even after the heading was applied.
        block = tag;
        // Read alignment off the SAME block that terminates the walk —
        // one DOM touch, no second pass.
        if (node.classList) {
          for (let i = 0; i < ALIGN_CLASSES.length; i++) {
            if (node.classList.contains(ALIGN_CLASSES[i])) { align = ALIGN_CLASSES[i]; break; }
          }
        }
        break;
      }
      node = node.parentElement;
    }
    // Clean up stale EMPTY highlight spans elsewhere in the editor.
    // Two sources of these orphans:
    //   (a) `<span class="ir-pending-inline ir-hl-*">\u200B</span>` —
    //       primed but never used, left behind when caret moved away.
    //   (b) `<span class="ir-hl-*"></span>` — committed highlight
    //       wrappers that lost all their text (user deleted everything
    //       inside, or browser cloned the inline span on Enter).
    //
    // Why this matters: (a) was already handled in iteration_126 and
    // keeps the icon from staying falsely lit. (b) is new in this fix
    // — Chrome's `insertParagraph` command refuses to produce fresh
    // lines when the caret is chained through empty inline wrappers,
    // which made repeated Android Return presses stop expanding the
    // canvas after the first one. Sweeping these empty committed
    // wrappers removes the obstacle without altering any visible
    // text.
    //
    // The ancestors the caret is CURRENTLY inside are preserved so
    // a legitimately-primed or mid-edit highlight is not disturbed.
    try {
      const stale = editor.querySelectorAll('span[class*="ir-hl-"]');
      stale.forEach((s) => {
        if (s === pendingHlAncestor) return;
        if (s === committedHlAncestor) return;
        let hasHlClass = false;
        s.classList.forEach((c) => { if (c.indexOf("ir-hl-") === 0) hasHlClass = true; });
        if (!hasHlClass) return;
        const txt = (s.textContent || "").replace(new RegExp(ZWSP, "g"), "");
        if (txt.length === 0 && s.parentNode) s.parentNode.removeChild(s);
      });
    } catch {}
    // If the block walk didn't land on a P/H1/H2/H3 (e.g. caret inside a
    // <li>), fall back to the generic block-ancestor finder so we still
    // reflect alignment on list items.
    if (align === null) {
      const b = findAlignBlockAncestor(editor, range.startContainer);
      if (b && b.classList) {
        for (let i = 0; i < ALIGN_CLASSES.length; i++) {
          if (b.classList.contains(ALIGN_CLASSES[i])) { align = ALIGN_CLASSES[i]; break; }
        }
      }
    }
    // Default-block fallback — when the caret sits on a bare text node
    // or empty line (just a <br>) directly under the editor root, the
    // ancestor walk above finds NO P/H1/H2/H3/LI wrapper. Historically
    // that produced `block = null`, which left the P / H1 / H2 / H3
    // buttons dark even though the line is conceptually a plain
    // left-aligned paragraph. Treat the "no block ancestor" case as
    // P so the P button lights up and the user can see at a glance
    // which block type the caret is currently in. This is detector-
    // only; no formatting command or DOM mutation is affected.
    if (block === null) block = "P";
    setActive((prev) =>
      prev.bold === bold && prev.italic === italic && prev.underline === underline
        && prev.strike === strike && prev.link === link && prev.block === block
        && prev.aa === aa && prev.hl === hl && prev.align === align
        && prev.list === list
        ? prev
        : { bold, italic, underline, strike, link, block, aa, hl, align, list }
    );
  }, []);

  // Listen for selection + focus changes while the toolbar is open and
  // keep `active` in sync. Also refresh once after every DOM-mutating
  // tool action — `runExecCommand`, `wrapSelectionWithTag`, and the
  // history restore path all dispatch `input` events on the editor,
  // so a document-level `input` listener gives us a free hook with no
  // changes to applyFormat / applyLink / applyImage call sites.
  useEffect(() => {
    if (!isOpen) return;
    const onSel = () => refreshActive();
    const onFocusIn = () => refreshActive();
    const onInput = (e) => {
      // Only refresh when the input event originates from one of our
      // known editor targets — ignore stray inputs from title fields,
      // tag inputs, etc.
      const t = e.target;
      if (!t || !t.getAttribute) return;
      const id = t.getAttribute("data-testid");
      if (id && EDITOR_TESTIDS.includes(id)) {
        // No marker cleanup here — we deliberately leave the ZWSP
        // anchor inside freshly-created inline tags intact (mirrors how
        // H1 leaves a <br> placeholder in an empty heading). Mutating
        // text nodes under the caret mid-type caused the Capacitor
        // "Oldb" character-reorder bug and is the single reason the
        // previous attempts failed.
        requestAnimationFrame(refreshActive);
      }
    };
    document.addEventListener("selectionchange", onSel);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("input", onInput, true);
    // Prime on mount so the state is correct even if the caret is
    // already sitting inside a formatted run (e.g. reopening a note).
    refreshActive();
    return () => {
      document.removeEventListener("selectionchange", onSel);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("input", onInput, true);
    };
  }, [isOpen, refreshActive]);

  // ---- INLINE row keyboard interception ------------------------------------
  // When the caret is INSIDE an INLINE cell:
  //   • Enter → exit the row, drop a new <p> below, caret into it.
  //   • Backspace at offset 0 of a cell → prevent (keeps row intact).
  // Everything else routes through the normal editor keyboard path and
  // is unaffected. This is the ONLY global keyboard handler that
  // mutates content on behalf of INLINE rows.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      const ed = findActiveEditor();
      if (!ed || ed.tagName === "TEXTAREA") return;
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!ed.contains(range.startContainer)) return;

      // ---- INLINE path: caret inside an INLINE cell ----
      // Resolve the active cell from Selection (not e.target — the
      // outer editor root is the e.target for nested contenteditable
      // spans; fix root-caused in iteration_117).
      const cell = findInlineCell(ed, range.startContainer);
      if (cell) {
        if (e.key === "Enter" && !e.shiftKey) {
          const row = findInlineRow(ed, cell);
          if (row) {
            e.preventDefault();
            exitInlineRow(ed, row);
          }
          return;
        }
        if (e.key === "Backspace") {
          if (!range.collapsed) return;
          const text = (cell.textContent || "");
          const stripped = text.replace(new RegExp(ZWSP, "g"), "");
          if (stripped.length === 0) {
            // Cell empty — never let Backspace eat the cell scaffold.
            e.preventDefault();
            return;
          }
          const atStart = (range.startContainer === cell && range.startOffset === 0)
            || (range.startContainer.nodeType === Node.TEXT_NODE
                && range.startContainer.parentNode === cell
                && range.startOffset === 0
                && !range.startContainer.previousSibling);
          if (atStart) {
            e.preventDefault();
            return;
          }
        }
        return;
      }

      // ---- BLOCK Return-exit path ----
      // When Enter is pressed inside a BLOCK that carries an ir-align-*
      // class, the browser's default `insertParagraph` clones the block
      // tag AND its classes, so the new paragraph would inherit the
      // alignment. Per user spec: Return should "close" the aligned
      // block and start a fresh, LEFT-aligned paragraph.
      //
      // Implementation (minimum surgical change): let the browser do
      // its native split so inline formatting, caret position, and
      // text content are preserved exactly as the engine handles them.
      // Immediately after, strip the ir-align-* classes from the NEW
      // block (the one the caret now sits in). Zero impact on blocks
      // that didn't carry an alignment class.
      if (e.key === "Enter" && !e.shiftKey) {
        // ---- Wrap-box escape ----
        // When the caret is inside a `.ir-wrap-box` AND the direct
        // child block containing the caret is empty and is the LAST
        // child of the wrap-box, Return should EXIT the container and
        // move the caret into the next sibling paragraph OUTSIDE the
        // box. Mirrors classic blockquote-escape ergonomics so the
        // user can continue typing in a normal paragraph after the
        // wrap-box. `applyWrapText` guarantees a trailing sibling
        // paragraph exists; we also create one on the fly if a stale
        // document is missing it.
        try {
          let n = range.startContainer;
          if (n && n.nodeType === Node.TEXT_NODE) n = n.parentNode;
          let wrapBox = null;
          let curBlock = null;
          const BLOCK_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "UL", "OL", "DIV", "BLOCKQUOTE", "PRE"]);
          let walk = n;
          while (walk && walk !== ed) {
            if (walk.classList && walk.classList.contains("ir-wrap-box")) { wrapBox = walk; break; }
            if (!curBlock && walk.tagName && BLOCK_TAGS.has(walk.tagName)) curBlock = walk;
            walk = walk.parentNode;
          }
          if (wrapBox) {
            // curBlock must be a DIRECT child of wrapBox for a clean
            // escape (otherwise the caret is nested in a list item or
            // similar — let the native Return handle one more level
            // of exit first).
            const block = (curBlock && curBlock.parentNode === wrapBox) ? curBlock : null;
            const isEmpty = block
              && !(block.textContent || "").replace(new RegExp(ZWSP, "g"), "").length
              && !block.querySelector("img, input");
            const isLast = block && (block === wrapBox.lastElementChild);
            if (block && isEmpty && isLast) {
              e.preventDefault();
              e.stopPropagation();
              // Remove the empty tail/last block.
              wrapBox.removeChild(block);
              // Ensure a sibling paragraph exists AFTER the wrap-box.
              let target = wrapBox.nextSibling;
              const needsTarget = !target
                || (target.nodeType === Node.TEXT_NODE
                    && !(target.textContent || "").replace(new RegExp(ZWSP, "g"), "").length)
                || (target.nodeType === Node.ELEMENT_NODE
                    && !BLOCK_TAGS.has(target.tagName));
              if (needsTarget) {
                const outP = document.createElement("p");
                outP.appendChild(document.createElement("br"));
                if (wrapBox.nextSibling) ed.insertBefore(outP, wrapBox.nextSibling);
                else ed.appendChild(outP);
                target = outP;
              }
              // Seat the caret at the start of the external block.
              const nr = document.createRange();
              nr.setStart(target, 0);
              nr.setEnd(target, 0);
              sel.removeAllRanges();
              sel.addRange(nr);
              // Keep wrap-box valid: if it ended up with zero children
              // (shouldn't happen, but defensive), drop the box.
              if (!wrapBox.firstChild) {
                try { wrapBox.parentNode && wrapBox.parentNode.removeChild(wrapBox); } catch { /* noop */ }
              }
              ed.dispatchEvent(new Event("input", { bubbles: true }));
              return;
            }
          }
        } catch { /* non-fatal — fall through to native Return */ }

        // Pre-emptive empty-inline-wrapper strip. Chrome's native
        // `insertParagraph` refuses to open new paragraphs when the
        // caret is nested inside an empty inline wrapper — the user
        // sees "first Return expands one line, further Returns do
        // nothing" (reported for repeated Android Return presses in
        // the Edit Note editor; selecting a spelling replacement
        // "unstuck" it because `insertReplacementText` writes a real
        // TextNode and clears the empty wrapper state).
        //
        // Generalised from the previous ir-hl-*-only strip: walk UP
        // from the caret container, collect every empty inline
        // wrapper (<b>/<strong>, <i>/<em>, <u>, <s>/<strike>/<del>,
        // <span class="ir-pending-inline …">, <span class="ir-hl-…">,
        // <span class="ir-size-…">, <span class="ir-color-…">, <a>,
        // <mark>, <code>, <sub>, <sup>, <font>) up to the first block
        // ancestor (P/H1/H2/H3/LI/UL/OL/DIV/BLOCKQUOTE/PRE). Remove
        // the OUTERMOST empty inline wrapper (which drops any nested
        // empty wrappers along with it) and re-seat the caret at the
        // same position in its parent. We never call preventDefault,
        // so Chrome's native Enter still runs — it just now operates
        // on a clean caret position and produces a real new paragraph.
        //
        // Scoped strictly to EMPTY wrappers (text content === "" after
        // ZWSP stripping), so a <b>hello</b> or <span class="ir-hl-X">
        // text</span> is never touched — bold/italic/underline/strike
        // active-state, highlights, Aa size/color, and format-first
        // typing after any real character typed all continue to work
        // exactly as before.
        try {
          const BLOCK_SET = BLOCK_SET_FOR_INLINE_STRIP;
          const INLINE_WRAPPER_SET = INLINE_WRAPPER_SET_FOR_STRIP;
          const zwspRe = new RegExp(ZWSP, "g");
          let startEl = range.startContainer;
          if (startEl && startEl.nodeType === Node.TEXT_NODE) startEl = startEl.parentNode;
          let outerEmpty = null;
          let walk = startEl;
          while (walk && walk !== ed && walk.tagName && !BLOCK_SET.has(walk.tagName)) {
            if (INLINE_WRAPPER_SET.has(walk.tagName)) {
              const txt = (walk.textContent || "").replace(zwspRe, "");
              if (txt.length === 0) outerEmpty = walk;
            }
            walk = walk.parentNode;
          }
          if (outerEmpty && outerEmpty.parentNode) {
            const parent = outerEmpty.parentNode;
            const idx = Array.prototype.indexOf.call(parent.childNodes, outerEmpty);
            parent.removeChild(outerEmpty);
            const nr = document.createRange();
            nr.setStart(parent, idx);
            nr.setEnd(parent, idx);
            sel.removeAllRanges(); sel.addRange(nr);
          }
        } catch { /* non-fatal */ }
        const block = findAlignBlockAncestor(ed, range.startContainer);
        if (!block) return;
        const hadAlign = ALIGN_CLASSES.some((c) => block.classList.contains(c));
        if (!hadAlign) return;
        // Native split happens — then we fix up on the next frame.
        requestAnimationFrame(() => {
          try {
            const s2 = window.getSelection && window.getSelection();
            if (!s2 || s2.rangeCount === 0) return;
            const newBlock = findAlignBlockAncestor(ed, s2.getRangeAt(0).startContainer);
            if (newBlock && newBlock !== block) {
              ALIGN_CLASSES.forEach((c) => newBlock.classList.remove(c));
              if (newBlock.classList.length === 0) newBlock.removeAttribute("class");
              ed.dispatchEvent(new Event("input", { bubbles: true }));
            }
          } catch { /* non-fatal */ }
        });
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen]);



  // Track focus on the known Expanded Text Editor targets.
  useEffect(() => {
    if (!isOpen) return;
    const isEditorEl = (el) => {
      if (!el || !el.getAttribute) return false;
      const id = el.getAttribute("data-testid");
      return !!id && EDITOR_TESTIDS.includes(id);
    };
    const onFocusIn = (e) => { if (isEditorEl(e.target)) setEditorFocused(true); };
    const onFocusOut = (e) => {
      if (!isEditorEl(e.target)) return;
      // Small deferral so tapping a toolbar button (which momentarily
      // steals focus) doesn't collapse the toolbar mid-interaction.
      setTimeout(() => {
        const active = document.activeElement;
        if (!isEditorEl(active)) setEditorFocused(false);
      }, 80);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    // Prime state if the editor is already focused when the toolbar mounts.
    if (isEditorEl(document.activeElement)) setEditorFocused(true);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [isOpen]);

  // Detect soft keyboard visibility via visualViewport shrinkage.
  // A ~120px delta reliably distinguishes an open on-screen keyboard from
  // browser chrome / toolbars in both portrait and landscape.
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const check = () => {
      const delta = window.innerHeight - vv.height;
      setKeyboardUp(delta > 120);
    };
    check();
    vv.addEventListener("resize", check);
    vv.addEventListener("scroll", check);
    window.addEventListener("orientationchange", check);
    return () => {
      vv.removeEventListener("resize", check);
      vv.removeEventListener("scroll", check);
      window.removeEventListener("orientationchange", check);
    };
  }, [isOpen]);

  // ------------------------------------------------------------------------
  // Manual JS-driven tool-strip scroll.
  // Radix Dialog wraps its content in `react-remove-scroll`, which installs
  // a document-level `touchmove` listener that calls preventDefault() on
  // any touch NOT inside its explicit `shards` list. Our toolbar is
  // portalled to `document.body` (to escape the Dialog's stacking context)
  // and therefore is not a shard — so `react-remove-scroll` kills the
  // browser's native pan-x / pan-y scroll for the tool strip. That is why
  // CSS-only fixes (`touch-action: pan-x`, `overflow-x: auto`, `flex-1`)
  // could satisfy structural tests yet fail on-device.
  //
  // Fix: bypass native scroll entirely for this scroller. Listen for touch
  // events on the scroller element itself and drive `scrollLeft` /
  // `scrollTop` from the finger delta. Programmatic scroll always works —
  // preventDefault only blocks the browser's default scroll GESTURE, not
  // scripted `Element.scrollLeft = ...` writes. Independent of Radix,
  // react-remove-scroll's shard list, and Dialog's event pipeline.
  // ------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const el = scrollerRef.current;
    if (!el) return;
    const isH = orientation === "horizontal";
    let state = null; // { x0, y0, sl0, st0, moved }

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) { state = null; return; }
      const t = e.touches[0];
      state = { x0: t.clientX, y0: t.clientY, sl0: el.scrollLeft, st0: el.scrollTop, moved: false };
    };
    const onTouchMove = (e) => {
      if (!state || !e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - state.x0;
      const dy = t.clientY - state.y0;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) state.moved = true;
      // Finger LEFT means content should scroll RIGHT (i.e. scrollLeft
      // increases). Standard direct-manipulation panning.
      if (isH) el.scrollLeft = state.sl0 - dx;
      else     el.scrollTop  = state.st0 - dy;
      // Once past the tap threshold, cancel the browser's own scroll
      // attempt AND the compat click that would otherwise fire on a
      // button under the finger at touchend.
      if (state.moved && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = () => {
      const s = state;
      state = null;
      if (s && s.moved) {
        // Swallow the one compat click that fires after a scroll gesture,
        // so a tool button doesn't accidentally activate when the user
        // was just panning.
        const stopOnce = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          el.removeEventListener("click", stopOnce, true);
        };
        el.addEventListener("click", stopOnce, true);
        setTimeout(() => el.removeEventListener("click", stopOnce, true), 400);
      }
    };

    // touchmove MUST be non-passive so preventDefault works. React's
    // synthetic touchmove is passive on some builds, hence native
    // addEventListener directly on the DOM node.
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    el.addEventListener("touchcancel",onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      el.removeEventListener("touchcancel",onTouchEnd);
    };
  }, [isOpen, orientation, pos]);


  // ------ Position: hover just above the on-screen keyboard ------
  // (Depends on `getUsableRect`, declared just below the visibility
  // gate effects but above this position effect — see below.)
  const rafRef = useRef(0);
  // Compute the usable rect the toolbar is allowed to occupy. This is
  // the host editor's visible container (Radix Dialog for NoteModal or
  // the `fullscreen-note` panel for FullScreenNote) intersected with
  // the visualViewport (so the on-screen keyboard shrinks the bottom
  // dynamically). Falls back to the layout viewport when neither host
  // is found (e.g. toolbar mounted outside a known container).
  const getUsableRect = useCallback(() => {
    const vv = window.visualViewport;
    const vvTop = vv?.offsetTop || 0;
    const vvBottom = vv ? vvTop + vv.height : window.innerHeight;
    let left = 0, right = window.innerWidth, top = 0, bottom = window.innerHeight;
    const host =
      document.querySelector('[role="dialog"][data-state="open"]') ||
      document.querySelector('[role="dialog"]') ||
      document.querySelector('[data-radix-dialog-content]') ||
      document.querySelector('[data-testid="fullscreen-note"]');
    if (host) {
      const r = host.getBoundingClientRect();
      left = r.left; right = r.right; top = r.top; bottom = r.bottom;
    }
    // Intersect with the visible viewport so the keyboard reduces the
    // bottom boundary and the toolbar can never be dragged under it.
    bottom = Math.min(bottom, vvBottom);
    top = Math.max(top, vvTop);
    // Guard against zero/negative sizes (dialog animating in).
    if (right - left < 40) { left = 0; right = window.innerWidth; }
    if (bottom - top < 40) { top = 0; bottom = window.innerHeight; }
    return { left, right, top, bottom };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const place = () => {
      // CRITICAL: do NOT clobber Y while the user is dragging the toolbar.
      // Without this gate, `visualViewport.scroll` events fire mid-drag on
      // iOS/Android and the Y snap yanks the toolbar back to the keyboard
      // line, which is what produced the "wig out" oscillation.
      if (dragStateRef.current.dragging) return;
      const rect = getUsableRect();
      const gap = isMobile ? 4 : 12; // "a few pixels" above the keyboard
      const height = orientation === "horizontal" ? HORIZONTAL_HEIGHT : 240;
      const width = orientation === "horizontal" ? Math.min(rect.right - rect.left - 24, 520) : VERTICAL_WIDTH;
      const centeredX = Math.max(rect.left + 8, Math.round(rect.left + (rect.right - rect.left - width) / 2));
      const y = Math.round(rect.bottom - height - gap);
      setPos(prev => {
        if (!prev) return { x: centeredX, y: Math.max(rect.top, y) };
        // Mobile: always snap Y to just-above-keyboard so the toolbar
        // visually travels with the keyboard. Keep X where the user
        // dragged, but re-clamp it against the current usable rect
        // (dialog resize, orientation change, keyboard resize).
        if (isMobile) {
          const maxX = Math.max(rect.left, rect.right - width);
          return {
            x: Math.max(rect.left, Math.min(maxX, prev.x)),
            y: Math.max(rect.top, y),
          };
        }
        // Desktop: preserve user-dragged position (existing behaviour).
        return prev;
      });
    };
    place();
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    window.addEventListener("orientationchange", place);
    return () => {
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
      window.removeEventListener("orientationchange", place);
    };
  }, [isOpen, orientation, isMobile, getUsableRect]);

  // ------ Drag by grip only ------
  // Uses pointer capture on the handle itself, so pointermove/pointerup
  // are guaranteed to fire on the handle even if the finger leaves its
  // bounds. All coordinates come from the SAME pointer event stream,
  // eliminating the "different pointer ids" jump artefact that produced
  // the wig-out.
  const onGripPointerDown = useCallback((e) => {
    if (!pos) return;
    e.preventDefault();
    e.stopPropagation();
    const handleEl = e.currentTarget;
    // Toolbar bbox — needed for containment so we clamp the whole box,
    // not just the top-left corner.
    const toolbarEl = handleEl.closest('[data-testid="floating-rte-toolbar"]');
    const tbRect = toolbarEl?.getBoundingClientRect();
    const tbW = tbRect ? tbRect.width : 100;
    const tbH = tbRect ? tbRect.height : 44;
    try { handleEl.setPointerCapture?.(e.pointerId); } catch {}
    dragStateRef.current = {
      dragging: true,
      pointerId: e.pointerId,
      offX: e.clientX - pos.x,
      offY: e.clientY - pos.y,
      tbW,
      tbH,
    };
    const move = (ev) => {
      if (!dragStateRef.current.dragging) return;
      if (ev.pointerId !== dragStateRef.current.pointerId) return;
      const cx = ev.clientX;
      const cy = ev.clientY;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const rect = getUsableRect();
        const w = dragStateRef.current.tbW;
        const h = dragStateRef.current.tbH;
        // Clamp the ENTIRE toolbar bounding box inside the usable rect.
        // If the toolbar is wider/taller than the rect (unusual, tiny
        // dialogs), pin to the top-left corner rather than producing NaN.
        const maxX = Math.max(rect.left, rect.right - w);
        const maxY = Math.max(rect.top, rect.bottom - h);
        const nextX = Math.max(rect.left, Math.min(maxX, cx - dragStateRef.current.offX));
        const nextY = Math.max(rect.top,  Math.min(maxY, cy - dragStateRef.current.offY));
        setPos({ x: nextX, y: nextY });
      });
    };
    const end = (ev) => {
      if (ev.pointerId !== dragStateRef.current.pointerId) return;
      dragStateRef.current.dragging = false;
      dragStateRef.current.pointerId = null;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      try { handleEl.releasePointerCapture?.(ev.pointerId); } catch {}
      handleEl.removeEventListener("pointermove", move);
      handleEl.removeEventListener("pointerup", end);
      handleEl.removeEventListener("pointercancel", end);
    };
    handleEl.addEventListener("pointermove", move);
    handleEl.addEventListener("pointerup", end);
    handleEl.addEventListener("pointercancel", end);
  }, [pos, getUsableRect]);

  // ------ Tool actions — REAL rich text, no Markdown fallback ------
  const stub = (name) => () => toast.info(`${name} — coming in a follow-up`, { duration: 1800 });

  // Unified format entry point. `format` is a semantic key
  // ("bold" | "italic" | "underline" | "strike" | "h1" | "h2" | "h3"
  //  | "ul" | "ol" | "checklist" | "undo" | "redo")
  // Branches on the live editor kind:
  //   • <textarea>       → promote note to HTML with real tags.
  //   • contentEditable  → native execCommand.
  const applyFormat = useCallback((format) => {
    const editor = findActiveEditor();
    if (!editor) return;

    if (editor.tagName === "TEXTAREA") {
      // Fix E.2 — the textarea branch reads selectionStart / End from
      // the stash because Android touchstart may have blurred the
      // textarea before `click` fires. Textareas preserve those offsets
      // across focus loss, but only on the element that owned the
      // selection — so if focus has drifted elsewhere the live editor
      // may be undefined. Prefer the stashed editor + offsets when they
      // match the resolved editor.
      if (SELECTION_STASH.editor === editor && SELECTION_STASH.range === null) {
        try {
          editor.focus();
          editor.setSelectionRange(SELECTION_STASH.start, SELECTION_STASH.end);
        } catch { /* noop */ }
      }
      // Promote helpers now return a selection-restoration mode string
      // ("range" | "collapsed-end") instead of a plain boolean — see Fix B
      // (preserve selection across textarea→HTML mount swap). We forward
      // that mode to focusHtmlEditorSoon so H1 → H2 → H3 chaining and
      // consecutive inline formats stop misfiring on the Edit Text path.
      let mode = false;
      if (INLINE_TAG[format]) mode = promoteTextareaWithInlineFormat(editor, format);
      else if (BLOCK_TAG[format]) mode = promoteTextareaWithBlockFormat(editor, format);
      else if (format === "undo" || format === "redo") {
        // Native textarea undo/redo is browser-controlled — no-op here.
        toast.info(`${format[0].toUpperCase()}${format.slice(1)} works in HTML mode`, { duration: 1500 });
        return;
      } else if (format === "p") {
        // Already plaintext — "P" is the implicit mode. No-op.
        return;
      } else {
        // Lists, Checklist, Clear-format etc. — require HTML mode. Prompt
        // the user to apply any inline format first (which auto-promotes).
        toast.info("Add some text and select it first", { duration: 1500 });
        return;
      }
      if (mode) focusHtmlEditorSoon(mode);
      return;
    }

    // contentEditable — native execCommand path.
    // Fix E.2 — restore stashed selection if Android touchstart stole
    // focus between pointerdown and click. No-op if the live selection
    // is still intact (desktop + well-behaved mobile paths).
    restoreStashedSelectionIfNeeded(editor);
    const execMap = {
      bold: ["bold"], italic: ["italic"], underline: ["underline"], strike: ["strikeThrough"],
      h1: ["formatBlock", "H1"], h2: ["formatBlock", "H2"], h3: ["formatBlock", "H3"],
      p: ["formatBlock", "P"],
      ul: ["insertUnorderedList"], ol: ["insertOrderedList"],
      clearFormat: ["removeFormat"],
    };
    const history = historyRef.current;
    // Match H1/H2/H3's reliable mechanism for inline formats with a
    // collapsed caret. H1 works because its path puts the caret INSIDE
    // a real `<h1>` element — typing extends that element. We do the
    // exact same thing for B / I / U / S:
    //   • Textarea: already handled via the Fix-D "pending-inline:*"
    //     path which emits `<b class="ir-caret-target">\u200B</b>` and
    //     (post this change) KEEPS the tag intact with caret inside.
    //   • ContentEditable: insert `<b>\u200B</b>` at the caret, place
    //     caret right after the ZWSP, and let the user type into the
    //     tag. No execCommand pending-state, no mutations during type.
    //
    // Toggle-off: if the caret is already inside a matching ancestor
    // (<b>/<strong>/<em>/<i>/<u>/<s>), position the caret just after
    // the ancestor in its parent — same visible result as "stop typing
    // bold". We insert a ZWSP sibling after the ancestor to give Chrome
    // a stable text anchor outside the tag.
    if (format === "bold" || format === "italic" || format === "underline" || format === "strike") {
      const liveSel = window.getSelection && window.getSelection();
      const liveRange = liveSel && liveSel.rangeCount > 0 ? liveSel.getRangeAt(0) : null;
      if (liveRange && liveRange.collapsed && editor.contains(liveRange.startContainer)) {
        const tag = INLINE_TAG[format];
        const matchTags = { bold: ["B","STRONG"], italic: ["I","EM"], underline: ["U"], strike: ["S","STRIKE","DEL"] };
        // Walk ancestors looking for a matching inline tag.
        let node = liveRange.startContainer;
        if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
        let ancestor = null;
        while (node && node !== editor) {
          if (node.tagName && matchTags[format].includes(node.tagName)) { ancestor = node; break; }
          node = node.parentElement;
        }
        if (history) history.snapshotImmediate(editor);
        try {
          if (ancestor) {
            // Toggle-off: place a ZWSP anchor just after the ancestor
            // in its parent and park the caret in it. User's next
            // keystrokes land in the parent, outside the format.
            const parent = ancestor.parentNode;
            if (parent) {
              const anchor = document.createTextNode(ZWSP);
              if (ancestor.nextSibling) parent.insertBefore(anchor, ancestor.nextSibling);
              else parent.appendChild(anchor);
              const nr = document.createRange();
              nr.setStart(anchor, 1); nr.setEnd(anchor, 1);
              liveSel.removeAllRanges(); liveSel.addRange(nr);
            }
          } else {
            // Format-switch: when the caret sits inside a DIFFERENT
            // inline-format ancestor (user tapped B, typed, now taps I)
            // the new format must become a SIBLING, not a nested child.
            // Walk outward to the outermost inline ancestor and emerge
            // from it before inserting the fresh pending marker. If
            // that outermost ancestor is itself an empty pending marker
            // (user tapped format but never typed), dissolve it so the
            // caret can take its slot.
            const INLINE_FMT_TAGS = { B: 1, STRONG: 1, I: 1, EM: 1, U: 1, S: 1, STRIKE: 1, DEL: 1 };
            let walk = liveRange.startContainer;
            if (walk && walk.nodeType === Node.TEXT_NODE) walk = walk.parentElement;
            let outermost = null;
            while (walk && walk !== editor && walk.tagName && INLINE_FMT_TAGS[walk.tagName]) {
              outermost = walk;
              walk = walk.parentElement;
            }
            let insertRange = liveRange;
            if (outermost) {
              const parent = outermost.parentNode;
              if (parent) {
                const outerText = outermost.textContent || "";
                const outerIsPending = outermost.classList && outermost.classList.contains(PENDING_INLINE_CLASS);
                const isOutermostEmpty = (outerText === ZWSP || outerText === "") && outerIsPending;
                let newIdx;
                if (isOutermostEmpty) {
                  const outermostIdx = Array.prototype.indexOf.call(parent.childNodes, outermost);
                  parent.removeChild(outermost);
                  newIdx = outermostIdx;
                } else {
                  // Remove any empty pending descendants left behind by
                  // a previous same-sequence tap (e.g. stale <em>​</em>
                  // inside <b>Bold</b> when user taps U after I).
                  const pendingInside = Array.from(outermost.querySelectorAll("." + PENDING_INLINE_CLASS));
                  pendingInside.forEach((m) => {
                    const t = m.textContent || "";
                    if (t === ZWSP || t === "") {
                      try { m.parentNode && m.parentNode.removeChild(m); } catch {}
                    }
                  });
                  const outermostIdx = Array.prototype.indexOf.call(parent.childNodes, outermost);
                  newIdx = outermostIdx + 1;
                }
                const safeIdx = Math.min(Math.max(newIdx, 0), parent.childNodes.length);
                const nr = document.createRange();
                nr.setStart(parent, safeIdx);
                nr.setEnd(parent, safeIdx);
                liveSel.removeAllRanges(); liveSel.addRange(nr);
                insertRange = nr;
              }
            }
            // Fresh inline: insert `<tag class="ir-pending-inline">\u200B</tag>`
            // at the (possibly-updated) caret position and place caret
            // inside at offset 1 (after ZWSP). Typing extends this tag.
            // The ir-pending-inline class marks it as a "format-first"
            // marker so a subsequent format-switch can dissolve it when
            // still empty.
            const marker = document.createElement(tag);
            marker.setAttribute("class", PENDING_INLINE_CLASS);
            marker.appendChild(document.createTextNode(ZWSP));
            insertRange.insertNode(marker);
            const nr = document.createRange();
            nr.setStart(marker.firstChild, 1);
            nr.setEnd(marker.firstChild, 1);
            liveSel.removeAllRanges(); liveSel.addRange(nr);
          }
          editor.dispatchEvent(new Event("input", { bubbles: true }));
        } catch { /* fall through to native path below */ }
        if (history) history.snapshotImmediate(editor);
        return;
      }
    }
    // Strikethrough gets a deterministic Range-based wrap (see
    // `wrapSelectionWithTag`). Everything else stays on execCommand so
    // Bold / Italic / Underline / Headings / Lists behave exactly as
    // before.
    //
    // Fix F — toggle behaviour. If the selection already lives inside
    // an existing <s>/<strike>/<del>, re-tapping Strike must REMOVE it
    // instead of wrapping again. We check via `active.strike` (populated
    // by refreshActive's ancestor walk — the same mechanism that lights
    // up the toolbar button) which is the source of truth we already
    // trust elsewhere.
    if (format === "strike") {
      if (history) history.snapshotImmediate(editor);
      if (active.strike) {
        unwrapStrikeAtSelection(editor);
      } else {
        wrapSelectionWithTag(editor, "s");
      }
      if (history) history.snapshotImmediate(editor);
      return;
    }
    // Multi-level undo/redo — custom stack, NOT execCommand. See
    // HistoryStack above for why.
    if (format === "undo" || format === "redo") {
      if (!history) return;
      const entry = format === "undo" ? history.undo() : history.redo();
      if (!entry) {
        toast.info(format === "undo" ? "Nothing to undo" : "Nothing to redo", { duration: 1200 });
        return;
      }
      // Preserve scroll position across the full-DOM swap.
      // `editor.innerHTML = entry.html` wipes the contentEditable's
      // children, which forces every scroll container up the tree
      // (the editor itself when `canvasExpanded`, and the containing
      // DialogContent with `max-h-[85vh] overflow-y-auto`) to reset
      // `scrollTop` to 0 because the referenced content no longer
      // exists until the new DOM paints. Visually this reads as
      // "Redo jumped the editor to the top-left of the page".
      //
      // Capture scroll offsets on the editor and every ancestor whose
      // computed `overflow-y` is auto/scroll BEFORE the swap, restore
      // them AFTER `restoreSelection`. No API or behavior change — we
      // only put each affected container back where it was.
      const savedScrolls = [];
      try {
        savedScrolls.push({ el: editor, top: editor.scrollTop, left: editor.scrollLeft });
        let anc = editor.parentNode;
        while (anc && anc.nodeType === 1 && anc !== document.body) {
          const oy = (anc.ownerDocument && anc.ownerDocument.defaultView)
            ? anc.ownerDocument.defaultView.getComputedStyle(anc).overflowY
            : "";
          if (oy === "auto" || oy === "scroll") {
            savedScrolls.push({ el: anc, top: anc.scrollTop, left: anc.scrollLeft });
          }
          anc = anc.parentNode;
        }
      } catch { /* non-fatal */ }
      editor.innerHTML = entry.html;
      restoreSelection(editor, entry.sel);
      try {
        for (const s of savedScrolls) {
          if (s.el && typeof s.top === "number") {
            s.el.scrollTop = s.top;
            s.el.scrollLeft = s.left;
          }
        }
      } catch { /* non-fatal */ }
      // Notify React so NoteModal syncs its state + storage.
      editor.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }
    // Interactive checklist — real UL with class + per-LI data-ir-check.
    // We let execCommand build the <ul><li> skeleton, then upgrade it.
    if (format === "checklist") {
      if (history) history.snapshotImmediate(editor);
      runExecCommand(editor, "insertUnorderedList");
      // Find the UL that now contains the caret and upgrade it.
      try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          let node = sel.getRangeAt(0).startContainer;
          while (node && node !== editor && node.nodeName !== "UL") node = node.parentNode;
          if (node && node.nodeName === "UL") {
            node.classList.add("ir-checklist");
            node.querySelectorAll(":scope > li").forEach((li) => {
              if (!li.hasAttribute("data-ir-check")) li.setAttribute("data-ir-check", "0");
            });
            editor.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
      } catch { /* noop */ }
      if (history) history.snapshotImmediate(editor);
      return;
    }
    const spec = execMap[format];
    if (spec) {
      if (history) history.snapshotImmediate(editor);
      runExecCommand(editor, spec[0], spec[1] ?? null);
      // Fix G — defensive cleanup after formatBlock. Some Capacitor
      // Android WebView builds emit `<h1><strong>text</strong></h1>`
      // (or wrap the heading in <strong>) when `document.execCommand(
      // "formatBlock", "H1")` runs on a paragraph whose containing block
      // had any residual Bold pending-state. Desktop Chrome produces a
      // clean `<h1>text</h1>` so this didn't surface in the preview
      // tests. The user reported this exact symptom from on-device
      // testing ("H1 looks bold and the Bold button lights up").
      //
      // We only run this cleanup for the three heading commands, so
      // nothing else (Bold / Italic / Underline / Strike / Lists /
      // Clear-format) is affected. Inside the resulting heading we
      // unwrap any direct <b> or <strong> whose text content equals
      // the entire heading's text — that signature matches a "wrap
      // whole heading in redundant inline bold" state and never
      // matches a user-intentional partial-word bold inside a heading,
      // which would have a shorter textContent.
      if (format === "h1" || format === "h2" || format === "h3") {
        try {
          const headingTag = (BLOCK_TAG[format] || "").toUpperCase();
          // Find all headings in the editor; cleanup is cheap and
          // scoped by selector so other blocks are untouched.
          editor.querySelectorAll("h1,h2,h3").forEach((h) => {
            if (h.tagName !== headingTag) return;
            const headingText = (h.textContent || "").trim();
            if (!headingText) return;
            // Walk direct inline-bold children only; a nested word-
            // level bold would be deeper (e.g. inside a text run) and
            // thus not a direct child.
            Array.from(h.children).forEach((child) => {
              const t = child.tagName;
              if (t !== "B" && t !== "STRONG") return;
              const childText = (child.textContent || "").trim();
              // Only unwrap when the <strong> covers the whole heading
              // — that's the signature of unintentional/emitted bold.
              if (childText !== headingText) return;
              while (child.firstChild) h.insertBefore(child.firstChild, child);
              h.removeChild(child);
            });
          });
          editor.dispatchEvent(new Event("input", { bubbles: true }));
        } catch { /* noop — leave the DOM as execCommand produced it */ }
      }
      if (history) history.snapshotImmediate(editor);
    }
  }, [active.strike]);

  // Line break (§12) — inserts a <br> at caret without starting a new
  // paragraph/block, distinct from pressing Return which commits to the
  // next <p>.
  const insertLineBreak = useCallback(() => {
    const editor = findActiveEditor();
    if (!editor) return;
    if (editor.tagName === "TEXTAREA") {
      // Plain mode: insert a newline character at caret.
      const start = editor.selectionStart ?? 0;
      const end = editor.selectionEnd ?? 0;
      const next = editor.value.slice(0, start) + "\n" + editor.value.slice(end);
      reactSetValue(editor, next);
      editor.setSelectionRange(start + 1, start + 1);
      return;
    }
    // Fix — Toolbar buttons steal focus on pointerdown; the caret stash
    // is restored for every other format tool via `applyFormat`, but
    // `insertLineBreak` was going straight to `runExecCommand` with
    // whatever Selection the browser had after the focus bounce. When
    // the Selection collapsed to offset 0 of the editor root, the
    // execCommand inserted a <br> at the top of the document and the
    // caret jumped to the beginning. Restoring the stash FIRST keeps
    // the caret where the user actually tapped.
    try { restoreStashedSelectionIfNeeded(editor); } catch {}
    const history = historyRef.current;
    if (history) history.snapshotImmediate(editor);
    runExecCommand(editor, "insertLineBreak");
    if (history) history.snapshotImmediate(editor);
  }, []);

  // Copy (§14) — current selection, falls back to the whole editor if
  // nothing is selected. Writes HTML and plaintext to the clipboard so
  // rich-text destinations keep formatting.
  const doCopy = useCallback(async () => {
    const editor = findActiveEditor();
    if (!editor) return;
    const sel = window.getSelection && window.getSelection();
    let html = "", text = "";
    if (sel && sel.rangeCount && !sel.isCollapsed) {
      const range = sel.getRangeAt(0);
      const container = document.createElement("div");
      container.appendChild(range.cloneContents());
      html = container.innerHTML;
      text = container.textContent || "";
    } else {
      html = editor.innerHTML || editor.value || "";
      text = editor.textContent || editor.value || "";
    }
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        })]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        document.execCommand("copy");
      }
      toast.success("Copied", { duration: 1000 });
    } catch {
      toast.error("Copy blocked by the browser", { duration: 1500 });
    }
  }, []);

  // Paste (§15) — reads clipboard and inserts at caret. Prefers HTML
  // (sanitized) when available, falls back to plaintext.
  const doPaste = useCallback(async () => {
    const editor = findActiveEditor();
    if (!editor) return;
    try {
      let html = "", text = "";
      if (navigator.clipboard && navigator.clipboard.read) {
        try {
          const items = await navigator.clipboard.read();
          for (const item of items) {
            if (item.types.includes("text/html") && !html) {
              const blob = await item.getType("text/html");
              html = await blob.text();
            }
            if (item.types.includes("text/plain") && !text) {
              const blob = await item.getType("text/plain");
              text = await blob.text();
            }
          }
        } catch { /* permission denied — fall back below */ }
      }
      if (!text && navigator.clipboard?.readText) {
        try { text = await navigator.clipboard.readText(); } catch { /* ignore */ }
      }
      if (!html && !text) {
        toast.error("Clipboard permission denied", { duration: 1500 });
        return;
      }
      const history = historyRef.current;
      if (editor.tagName === "TEXTAREA") {
        const start = editor.selectionStart ?? 0;
        const end = editor.selectionEnd ?? 0;
        const next = editor.value.slice(0, start) + (text || "") + editor.value.slice(end);
        reactSetValue(editor, next);
        editor.setSelectionRange(start + text.length, start + text.length);
      } else {
        if (history) history.snapshotImmediate(editor);
        if (html) {
          runExecCommand(editor, "insertHTML", sanitizeHtml(html));
        } else {
          runExecCommand(editor, "insertText", text);
        }
        if (history) history.snapshotImmediate(editor);
      }
    } catch {
      toast.error("Paste failed", { duration: 1500 });
    }
  }, []);

  // Export current note as PDF (§18) via the browser's print pipeline.
  // Opens a hidden iframe populated with the note's HTML + minimal
  // styling, then calls `print()`. User picks "Save as PDF" in the
  // native print dialog. Zero new dependencies, works offline.
  const exportPdf = useCallback(() => {
    const editor = findActiveEditor();
    if (!editor) return;
    const html = editor.tagName === "TEXTAREA"
      ? plainTextToHtml(editor.value || "")
      : (editor.innerHTML || "");
    const titleEl =
      document.querySelector('[data-testid="note-title-input"]') ||
      document.querySelector('[data-testid="fullscreen-title-input"]');
    const title = titleEl?.value || "Note";
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups and try again", { duration: 2500 });
      return;
    }
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title.replace(/</g, "&lt;")}</title>
<style>
  body { font-family: -apple-system, system-ui, sans-serif; color: #000; padding: 2rem; max-width: 7in; margin: auto; line-height: 1.5; }
  h1 { font-size: 2em; margin: 0.8em 0 0.4em; }
  h2 { font-size: 1.5em; margin: 0.7em 0 0.3em; }
  h3 { font-size: 1.2em; margin: 0.6em 0 0.2em; }
  p  { margin: 0.4em 0; }
  ul, ol { margin: 0.4em 0 0.4em 1.5em; }
  ul.ir-checklist { list-style: none; padding-left: 0; }
  ul.ir-checklist > li { padding-left: 1.5em; position: relative; }
  ul.ir-checklist > li::before { content: "☐"; position: absolute; left: 0; }
  ul.ir-checklist > li[data-ir-check="1"]::before { content: "☑"; }
  ul.ir-checklist > li[data-ir-check="1"] { opacity: 0.65; text-decoration: line-through; }
  a { color: #0366d6; }
  @media print { @page { margin: 0.6in; } }
</style></head><body><h1>${title.replace(/</g, "&lt;")}</h1>${sanitizeHtml(html)}</body></html>`);
    w.document.close();
    // Give the new window a moment to lay out before invoking print.
    setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 250);
  }, []);

  const applyLink = useCallback(() => {
    const editor = findActiveEditor();
    if (!editor) return;
    // Figure out whether the user already has a selection; if yes, use
    // the selected text as the link label and only prompt for URL. If
    // no selection, prompt for BOTH label and URL (spec §17).
    let hasSelection = false, selText = "";
    if (editor.tagName === "TEXTAREA") {
      hasSelection = (editor.selectionEnd ?? 0) > (editor.selectionStart ?? 0);
      if (hasSelection) selText = editor.value.slice(editor.selectionStart, editor.selectionEnd);
    } else {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.rangeCount && !sel.isCollapsed) {
        hasSelection = true; selText = sel.toString();
      }
    }
    let label = selText;
    if (!hasSelection) {
      label = window.prompt("Link text") || "";
      if (!label) return;
    }
    const url = window.prompt("Enter URL", "https://");
    if (!url) return;
    const safeUrl = url.replace(/"/g, "&quot;");
    const history = historyRef.current;
    if (editor.tagName === "TEXTAREA") {
      const start = editor.selectionStart ?? 0;
      const end = editor.selectionEnd ?? 0;
      const value = editor.value || "";
      const before = value.slice(0, start);
      const after = value.slice(end);
      const marked = before + MARK_OPEN + label + MARK_CLOSE + after;
      let html = plainTextToHtml(marked);
      html = html.split(MARK_OPEN).join(`<a href="${safeUrl}">`).split(MARK_CLOSE).join("</a>");
      reactSetValue(editor, html);
      focusHtmlEditorSoon();
      return;
    }
    if (history) history.snapshotImmediate(editor);
    if (hasSelection) {
      runExecCommand(editor, "createLink", url);
    } else {
      // Insert new <a>text</a> at caret.
      runExecCommand(editor, "insertHTML", `<a href="${safeUrl}">${label.replace(/</g, "&lt;")}</a>`);
    }
    if (history) history.snapshotImmediate(editor);
  }, []);

  const applyImage = useCallback(() => {
    const src = window.prompt("Image URL (public link)");
    if (!src) return;
    const editor = findActiveEditor();
    if (!editor) return;
    if (editor.tagName === "TEXTAREA") {
      toast.info("Add some text first, then insert image in Format mode", { duration: 1800 });
      return;
    }
    runExecCommand(editor, "insertImage", src);
  }, []);

  // WrapText — rounded-corner inline container.
  //
  // User-visible behavior (per spec):
  //   • Select text → tap Wrap → the selection is enclosed in a
  //     `<span class="ir-wrap-box">` AT ITS ORIGINAL LOCATION. Nothing
  //     is moved, nothing is appended elsewhere; the span simply
  //     surrounds the extracted range contents and is re-inserted in
  //     place.
  //   • No selection → brief toast, no mutation.
  //   • Selection already inside an existing `.ir-wrap-box` → toast
  //     "Already wrapped" and bail (no nested duplicates).
  //
  // Implementation notes:
  //   • `<span>` (not `<div>`) with CSS `box-decoration-break: clone`
  //     + `display: inline` so the rounded box flows with surrounding
  //     text and wraps naturally on narrow mobile viewports without
  //     shifting the paragraph.
  //   • `extractContents()` → `appendChild` preserves nested
  //     formatting (H1/H2/H3/B/I/U/S/HL/lists where structurally
  //     valid inside the span).
  //   • Caret re-seated AFTER the wrapper so continued typing lands
  //     outside the box (matches blockquote-style ergonomics).
  //   • Snapshots the editor state immediately before + after via the
  //     existing HistoryStack, so this operation participates in the
  //     existing Undo/Redo path without touching that implementation.
  //   • Dispatches an `input` event so NoteModal syncs its React
  //     state and persists via the normal Save path.
  // WrapText — ONE continuous rounded block container per selection.
  //
  // Visible contract (final):
  //   • Wrap entire selection in a SINGLE `<div class="ir-wrap-box">`
  //     regardless of how many lines/paragraphs it spans. Never one
  //     container per visual line.
  //   • Preserve selected content order, position, and inline
  //     formatting. Headings and lists survive wherever structurally
  //     valid inside a `<div>`.
  //   • Append a trailing empty `<p class="ir-wrap-tail">` INSIDE the
  //     container as the usable ~1–2 line tap/typing area. The
  //     container grows naturally with additional content; the tail
  //     is initial empty space, not a cap.
  //   • Caret seated inside the trailing paragraph on apply so the
  //     user can continue typing immediately.
  //   • Nested-wrap guard: no duplicate wrappers.
  //
  // Textarea path (new notes): emits final HTML via `reactSetValue`
  // so React swaps textarea → HTML editor; caret seated after mount.
  // HTML path (existing notes): `extractContents()` → normalise →
  // `execCommand("insertHTML", …)` which handles the block-split
  // when the collapsed cursor sits inside a `<p>`/`<h1>`.
  //
  // Snapshots the editor via HistoryStack before + after so Undo/Redo
  // participate. Dispatches a bubbling `input` so NoteModal syncs
  // React state and persists via the existing Save path.
  const applyWrapText = useCallback(() => {
    const editor = findActiveEditor();
    if (!editor) return;
    try { restoreStashedSelectionIfNeeded(editor); } catch { /* noop */ }

    // ─── Textarea (new-note) path.
    if (editor.tagName === "TEXTAREA") {
      const start = editor.selectionStart ?? 0;
      const end = editor.selectionEnd ?? 0;
      if (start === end) {
        toast.info("Select text first, then tap Wrap", { duration: 1600 });
        return;
      }
      const value = editor.value || "";
      const before = value.slice(0, start);
      const middle = value.slice(start, end);
      const after = value.slice(end);
      const beforeHtml = plainTextToHtml(before);
      const middleHtml = plainTextToHtml(middle) || "<p></p>";
      // Always append a trailing paragraph OUTSIDE the wrap-box so the
      // caret has a sibling to escape into when the user presses Return
      // at the end of the box. Without this, wrapping the final line of
      // a note left no block-level target outside the container and the
      // caret stayed trapped inside .ir-wrap-tail.
      const afterHtml = plainTextToHtml(after) || "<p><br></p>";
      const rawHtml =
        beforeHtml +
        `<div class="ir-wrap-box ${CARET_TARGET_CLASS}">${middleHtml}<p class="ir-wrap-tail"><br></p></div>` +
        afterHtml;
      // Edit Note only: seat the "Tap to write" separators immediately.
      const html = editor.getAttribute("data-testid") === "note-content-input" ? markWrapSeparators(rawHtml) : rawHtml;
      reactSetValue(editor, html);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const el = findActiveHtmlEditor();
            if (!el || typeof el.focus !== "function") return;
            el.focus();
            const wrap = el.querySelector(`.${CARET_TARGET_CLASS}`);
            if (!wrap) return;
            wrap.classList.remove(CARET_TARGET_CLASS);
            const tail = wrap.querySelector("p.ir-wrap-tail");
            if (!tail) return;
            const sel = window.getSelection && window.getSelection();
            if (!sel) return;
            const r = document.createRange();
            r.setStart(tail, 0);
            r.setEnd(tail, 0);
            sel.removeAllRanges();
            sel.addRange(r);
          } catch { /* non-fatal */ }
        });
      });
      return;
    }

    // ─── HTML (contentEditable, existing-note) path.
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      toast.info("Select text first, then tap Wrap", { duration: 1600 });
      return;
    }
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) {
      toast.info("Select text first, then tap Wrap", { duration: 1600 });
      return;
    }
    const findWrapAncestor = (node) => {
      let n = node;
      if (n && n.nodeType === Node.TEXT_NODE) n = n.parentNode;
      while (n && n !== editor) {
        if (n.classList && n.classList.contains("ir-wrap-box")) return n;
        n = n.parentNode;
      }
      return null;
    };
    if (findWrapAncestor(range.startContainer) || findWrapAncestor(range.endContainer)) {
      toast.info("Already inside a wrap container", { duration: 1600 });
      return;
    }

    const history = historyRef.current;
    if (history) history.snapshotImmediate(editor);
    try {
      const fragment = range.extractContents();
      // Normalise: if the fragment has no block-level children, wrap
      // the inline/text content in a single <p>.
      const BLOCK_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "LI", "DIV", "BLOCKQUOTE", "PRE"]);
      let hasBlock = false;
      for (const c of Array.from(fragment.children)) {
        if (BLOCK_TAGS.has(c.tagName)) { hasBlock = true; break; }
      }
      // Build the wrap-div natively (no execCommand — Chrome's
      // insertHTML strips the outer <div> when inserting inside a <p>).
      const wrap = document.createElement("div");
      wrap.className = "ir-wrap-box";
      if (hasBlock) {
        wrap.appendChild(fragment);
      } else {
        const p = document.createElement("p");
        p.appendChild(fragment);
        wrap.appendChild(p);
      }
      const tailP = document.createElement("p");
      tailP.className = "ir-wrap-tail";
      tailP.appendChild(document.createElement("br"));
      wrap.appendChild(tailP);

      // Find the top-level block ancestor of the collapsed cursor —
      // the direct editor child. If none, cursor is at editor root
      // and we insert directly.
      let topBlock = range.startContainer;
      if (topBlock && topBlock.nodeType === Node.TEXT_NODE) topBlock = topBlock.parentNode;
      while (topBlock && topBlock.parentNode && topBlock.parentNode !== editor) {
        topBlock = topBlock.parentNode;
      }
      const inSingleBlock = !!(topBlock && topBlock !== editor && topBlock.parentNode === editor);

      if (inSingleBlock) {
        // Split topBlock at the collapsed range so the wrap-div lands
        // as a clean sibling at the editor root (not nested inside a
        // <p>/<h1> which would be invalid HTML and render oddly).
        const splitEnd = document.createRange();
        splitEnd.setStart(range.startContainer, range.startOffset);
        splitEnd.setEnd(topBlock, topBlock.childNodes.length);
        const afterFrag = splitEnd.extractContents();
        const afterBlock = topBlock.cloneNode(false); // same tag, no children, keeps classes
        afterBlock.removeAttribute("id");
        afterBlock.appendChild(afterFrag);
        // Insert the afterBlock, then the wrap-div BEFORE it so the
        // final order is: topBlock(before) → wrap → afterBlock(after).
        if (topBlock.nextSibling) {
          editor.insertBefore(afterBlock, topBlock.nextSibling);
        } else {
          editor.appendChild(afterBlock);
        }
        editor.insertBefore(wrap, afterBlock);
        // Clean up empty halves (no visible content).
        const emptyish = (el) => !el.textContent && !el.querySelector("img, br, input");
        if (emptyish(topBlock)) editor.removeChild(topBlock);
        if (emptyish(afterBlock)) editor.removeChild(afterBlock);
      } else {
        // Cursor at editor root (multi-block selection extracted both
        // partial blocks were preserved as siblings; the collapsed
        // range sits between them). Direct insertion is clean.
        range.insertNode(wrap);
      }

      // Enforce the editor-wide "editable gap around wrap-boxes" invariant
      // via the shared normaliser — strictly additive, idempotent, covers
      // between-boxes AND trailing-after-last-box in one pass. Replaces
      // the earlier ad-hoc trailing-sibling snippet.
      try {
        if (editor.getAttribute("data-testid") === "note-content-input-html") markWrapSeparatorsDom(editor);
        else normalizeWrapBoxSpacingDom(editor);
      } catch { /* non-fatal */ }

      // Caret inside the trailing paragraph for seamless typing in
      // the empty tap area.
      const tail = wrap.querySelector("p.ir-wrap-tail");
      if (tail) {
        const r = document.createRange();
        r.setStart(tail, 0);
        r.setEnd(tail, 0);
        sel.removeAllRanges();
        sel.addRange(r);
      }
    } catch { /* non-fatal — leave DOM untouched */ }
    if (history) history.snapshotImmediate(editor);
    editor.dispatchEvent(new Event("input", { bubbles: true }));
  }, []);

  // ------ Tool descriptor list ------
  const groups = useMemo(() => [
    {
      label: "text",
      tools: [
        // Aa — opens the floating Text Appearance dropdown (size + color
        // + clear-format). Floating overlay; does not shift the toolbar
        // or editor. See AppearancePanel below for the panel itself.
        { id: "aa", icon: CaseSensitive, title: "Aa — Text appearance",
          className: "ir-aa-tool-btn",
          onClick: (e) => {
            try {
              const rect = e && e.currentTarget && e.currentTarget.getBoundingClientRect();
              setAaPanel(rect ? { anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height } } : { anchor: null });
            } catch { setAaPanel({ anchor: null }); }
          },
        },
        { id: "p",  icon: Pilcrow, title: "Paragraph", onClick: () => applyFormat("p") },
        { id: "h1", icon: Heading1, title: "Heading 1", onClick: () => applyFormat("h1") },
        { id: "h2", icon: Heading2, title: "Heading 2", onClick: () => applyFormat("h2") },
        { id: "h3", icon: Heading3, title: "Heading 3", onClick: () => applyFormat("h3") },
        { id: "bold", icon: Bold, title: "Bold", onClick: () => applyFormat("bold") },
        { id: "italic", icon: Italic, title: "Italic", onClick: () => applyFormat("italic") },
        { id: "underline", icon: Underline, title: "Underline", onClick: () => applyFormat("underline") },
        { id: "strike", icon: Strikethrough, title: "Strikethrough", onClick: () => applyFormat("strike") },
        // (color moved into Aa dropdown — removed from the toolbar row
        // to keep the main toolbar lean. `highlight` remains as a future
        // extension slot.)
        // Highlighter — floating palette for text-BACKGROUND color.
        // Mirrors the Aa panel infrastructure (portal + overlay +
        // touch-isolation) but shows only a 16-swatch grid.
        { id: "highlight", icon: Highlighter, title: "Highlight",
          onClick: (e) => {
            // Toggle semantics: if the Highlighter is currently ACTIVE
            // on the caret (i.e. caret is inside an ir-hl-* span, or an
            // ir-pending-inline ir-hl-* marker), tapping again turns
            // highlighting OFF immediately — DO NOT open the palette.
            // Existing highlighted text elsewhere in the doc is NOT
            // removed; only the caret's current highlight state exits.
            try {
              const ed = findActiveEditor();
              if (ed) {
                try { restoreStashedSelectionIfNeeded(ed); } catch {}
                if (readActiveHighlight(ed)) {
                  toggleHighlightOff(ed);
                  try { stashSelection(); } catch {}
                  return;
                }
              }
            } catch { /* fall through to palette-open path */ }
            // Inactive → open the color palette as before.
            try {
              const rect = e && e.currentTarget && e.currentTarget.getBoundingClientRect();
              setHlPanel(rect ? { anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height } } : { anchor: null });
            } catch { setHlPanel({ anchor: null }); }
          },
        },
        { id: "br", icon: CornerDownLeft, title: "Line break", onClick: insertLineBreak },
      ],
    },
    {
      label: "lists",
      tools: [
        { id: "align", icon: AlignLeft, title: "Alignment",
          onClick: (e) => {
            try {
              const rect = e && e.currentTarget && e.currentTarget.getBoundingClientRect();
              setAlignPanel(rect ? { anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height } } : { anchor: null });
            } catch { setAlignPanel({ anchor: null }); }
          },
        },
        { id: "wrap", icon: WrapText, title: "Wrap", onClick: applyWrapText },
        { id: "ul", icon: List, title: "Bulleted list", onClick: () => applyFormat("ul") },
        { id: "ol", icon: ListOrdered, title: "Numbered list", onClick: () => applyFormat("ol") },
        { id: "checklist", icon: ListChecks, title: "Checklist", onClick: () => applyFormat("checklist") },
      ],
    },
    {
      label: "history",
      tools: [
        { id: "undo", icon: Undo2, title: "Undo", onClick: () => applyFormat("undo") },
        { id: "redo", icon: Redo2, title: "Redo", onClick: () => applyFormat("redo") },
        { id: "cut", icon: Scissors, title: "Cut", stub: true },
        { id: "copy", icon: Copy, title: "Copy", onClick: doCopy },
        { id: "paste", icon: ClipboardPaste, title: "Paste", onClick: doPaste },
      ],
    },
    {
      label: "insert",
      tools: [
        { id: "link", icon: LinkIcon, title: "Insert link", onClick: applyLink },
        { id: "image", icon: ImageIcon, title: "Insert image", onClick: applyImage },
        { id: "hierarchy", icon: Network, title: "🌳 Hierarchy", onClick: () => setPickerOpen("hierarchy"), accent: true },
        { id: "insert-form", icon: ClipboardList, title: "Insert Form", onClick: () => setPickerOpen("forms"), accent: true },
      ],
    },
    {
      label: "capture",
      tools: [
        { id: "scan", icon: ScanLine, title: "Scan", stub: true },
        { id: "camera", icon: Camera, title: "Camera", stub: true },
        { id: "voice", icon: Mic, title: "Voice-to-text", stub: true },
        { id: "pdf", icon: FileText, title: "Export PDF", onClick: exportPdf },
        { id: "print", icon: Printer, title: "Print", stub: true },
        { id: "share", icon: Share2, title: "Share", stub: true },
      ],
    },
  ], [applyFormat, applyLink, applyImage, applyWrapText, insertLineBreak, doCopy, doPaste, exportPdf]);

  // Container-level `onClick` swallow ONLY. Earlier iterations also
  // swallowed pointerdown / mousedown / touchstart here to keep taps in
  // the gap gutters from leaking to elements underneath — but since we
  // portal to document.body with an explicit top z-index and the
  // container itself is opaque to pointer events (`pointerEvents:auto`),
  // the browser's hit test already routes every event in the toolbar
  // rect to the toolbar. Calling `stopPropagation` on `pointerdown` /
  // `touchstart` inside a scroll container can prevent Chromium and
  // WebKit's compositor from claiming the pointer stream for native
  // panning, which is exactly what was killing the tool-strip scroll
  // gesture on-device. So we keep just `onClick` swallow — a bubble-
  // phase click still fires AFTER the child button's own onClick, so it
  // never blocks legitimate button taps, but it prevents any stray
  // click on a gap gutter from bubbling out.
  const swallow = useCallback((e) => { e.stopPropagation(); }, []);

  if (!isOpen || !pos) return null;
  // Mobile visibility gate: only show when the Expanded Text Editor is
  // focused AND the on-screen keyboard is up. Desktop is unaffected.
  if (isMobile && (!editorFocused || !keyboardUp)) return null;

  // ------ Styling ------
  const isH = orientation === "horizontal";
  const glass = isDark
    ? "bg-black/55 backdrop-blur-md border-white/10"
    : "bg-white/70 backdrop-blur-md border-gray-300/70";
  const accentBorder = "ring-1 ring-orange-500/60";
  const iconColor = "text-orange-400";
  const stubColor = isDark ? "text-slate-500/70" : "text-slate-400/80";

  // Fix C — map a tool-id → the key on `active` state that governs its
  // highlight. Any id missing from this map is a non-highlightable tool
  // (undo/redo/copy/paste/link/image/etc.) and simply never activates.
  // Blocks (p/h1/h2/h3) read from `active.block`.
  const isToolActive = (id) => {
    switch (id) {
      case "bold": return !!active.bold;
      case "italic": return !!active.italic;
      case "underline": return !!active.underline;
      case "strike": return !!active.strike;
      case "p": return active.block === "P";
      case "h1": return active.block === "H1";
      case "h2": return active.block === "H2";
      case "h3": return active.block === "H3";
      case "link": return !!active.link;
      // Aa — lights whenever ANY appearance (size or color) is active
      // on the caret. Independent of P/H1/H2/H3/B/I/U/S.
      case "aa": return !!active.aa;
      case "highlight": return !!active.hl;
      case "align": return !!active.align;
      case "ul": return active.list === "ul";
      case "ol": return active.list === "ol";
      case "checklist": return active.list === "checklist";
      default: return false;
    }
  };

  const containerStyle = {
    left: pos.x,
    top: pos.y,
    position: "fixed",
    // z-index 2147483000 — sits above Radix Dialog portals (2147483647
    // reserved for browser UI) but stays comfortably below system chrome.
    // We portal to document.body below, but keep the explicit z-index so
    // stacking is deterministic if a future ancestor introduces a new
    // stacking context.
    zIndex: 2147483000,
    maxWidth: isH ? "calc(100vw - 24px)" : undefined,
    maxHeight: isH ? undefined : "calc(100vh - 24px)",
    // touchAction on the container is deliberately `auto` — the three
    // interaction zones each declare their own touch-action:
    //   • drag handle → `none`      (drag captures the pointer)
    //   • tool scroller → `pan-x`   (horizontal panning only)
    //   • buttons     → `manipulation` (fast tap, no double-tap zoom)
    // Setting `none` at the container would intersect with descendants
    // per the touch-action spec and defeat the scroller.
    touchAction: "auto",
    // Guarantee the toolbar is opaque to pointer events end-to-end so
    // taps in the ~4-6px gaps between icon buttons don't fall through
    // to the editor / modal buttons beneath.
    pointerEvents: "auto",
  };

  const toolbar = (
    <>
      <div
        data-testid="floating-rte-toolbar"
        className={`select-none flex ${isH ? "flex-row" : "flex-col"} items-stretch rounded-2xl border ${glass} ${accentBorder} shadow-2xl`}
        style={containerStyle}
        onClick={swallow}
      >
        {/* Fixed CONTROL ZONE — drag handle + orientation, ALWAYS at the
            leading edge (top in vertical, left in horizontal). Sits
            OUTSIDE the scrollable tool strip so it never scrolls away. */}
        <div
          data-testid="floating-rte-controls"
          className={`shrink-0 flex ${isH ? "flex-row" : "flex-col"} items-stretch ${isH ? "rounded-l-2xl border-r" : "rounded-t-2xl border-b"} ${isDark ? "border-white/10 bg-white/5" : "border-gray-300 bg-white/40"}`}
        >
          {/* Drag handle (☰) — the ONLY area used to reposition the toolbar. */}
          <button
            type="button"
            data-testid="floating-rte-drag-handle"
            onPointerDown={onGripPointerDown}
            onMouseDown={(e) => e.preventDefault()}
            aria-label="Move toolbar"
            className={`shrink-0 flex items-center justify-center w-10 h-11`}
            style={{ touchAction: "none", cursor: "grab" }}
          >
            <Menu size={18} className={isDark ? "text-slate-300" : "text-slate-700"} />
          </button>
          {/* Divider between handle and orientation control */}
          <div className={`${isH ? "w-px h-6 self-center" : "h-px w-6 self-center"} ${isDark ? "bg-white/10" : "bg-gray-300"}`} />
          {/* Orientation control — two-arrow icon indicating the target
              orientation. Horizontal shows ↕ (tap to go vertical),
              vertical shows ↔ (tap to go horizontal). NEVER a refresh
              icon. */}
          <button
            type="button"
            data-testid="floating-rte-rotate"
            // Fix A — mobile tap-focus theft. onMouseDown.preventDefault
            // alone is insufficient on Android Chrome: touchstart (and
            // the pointerdown that mirrors it) transfer focus BEFORE
            // mousedown fires. Adding onPointerDown.preventDefault moves
            // the focus-theft block earlier in the event sequence so the
            // editor's selection survives the tap. The browser still
            // dispatches `click` after pointerup, so onClick fires
            // normally on both touch and mouse paths.
            onPointerDown={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOrientation(o => o === "horizontal" ? "vertical" : "horizontal")}
            aria-label={isH ? "Switch to vertical toolbar" : "Switch to horizontal toolbar"}
            style={{ touchAction: "manipulation" }}
            className={`shrink-0 flex items-center justify-center w-10 h-11 hover:bg-white/10 active:scale-95 transition`}
          >
            {isH
              ? <ArrowUpDown    size={16} className={isDark ? "text-slate-300" : "text-slate-700"} />
              : <ArrowLeftRight size={16} className={isDark ? "text-slate-300" : "text-slate-700"} />}
          </button>
        </div>

        {/* Scrollable tool row — its own gesture zone.
            `touch-action: pan-x` (horizontal) / `pan-y` (vertical) lets the
            browser natively handle finger-scrolling here WITHOUT stealing
            drag from the handle in the control zone.

            CRITICAL sizing note: the scroller MUST have `flex-1` and
            `min-w-0` / `min-h-0` in its cross-axis, otherwise the flex
            algorithm sizes it to fit its ~1000px of tool content and
            `overflow-*: auto` has no viewport to overflow against — the
            container just grows off-screen instead of scrolling. Also
            constrain the main-axis dimension so the browser has a
            fixed-size viewport to pan against. */}
        <div
          ref={scrollerRef}
          data-testid="floating-rte-scroller"
          className={`flex ${isH
            ? "flex-row overflow-x-auto overflow-y-hidden min-w-0"
            : "flex-col overflow-y-auto overflow-x-hidden min-h-0"} flex-1`}
          style={{
            scrollbarWidth: "thin",
            WebkitOverflowScrolling: "touch",
            padding: 4,
            touchAction: isH ? "pan-x" : "pan-y",
            overscrollBehavior: "contain",
            // Explicit main-axis cap. Without this, the scroller can still
            // report its scrollWidth as its clientWidth on some engines
            // (Safari) and refuse to scroll. flex-1 sets flex-basis:0 so
            // the scroller stretches to fill remaining space in the
            // toolbar — that IS its viewport for overflow.
            ...(isH ? { maxWidth: "100%" } : { maxHeight: "100%" }),
          }}
        >
          {groups.map((g, gi) => (
            <div
              key={g.label}
              className={`flex ${isH ? "flex-row items-center" : "flex-col items-center"} shrink-0`}
              data-testid={`floating-rte-group-${g.label}`}
            >
              {g.tools.map((t) => {
                const Icon = t.icon;
                const handle = t.onClick || stub(t.title);
                const activeNow = isToolActive(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`floating-rte-btn-${t.id}`}
                    data-active={activeNow ? "true" : "false"}
                    title={t.title}
                    // Fix A — mobile tap-focus theft. See the rotate
                    // button above for the full rationale. In short:
                    // pointerdown fires BEFORE focus transfer on Android
                    // Chrome, so preventing default there is what keeps
                    // the editor's selection alive for the subsequent
                    // execCommand / wrap / promote operation.
                    //
                    // Fix E.2 — belt-and-suspenders: stash the editor's
                    // current selection synchronously here, BEFORE any
                    // compatibility event has a chance to collapse it.
                    // `applyFormat` restores from the stash if the live
                    // selection has drifted by the time `click` fires.
                    onPointerDown={(e) => {
                      stashSelection();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      stashSelection();
                      e.preventDefault();
                    }}
                    onClick={handle}
                    style={{ touchAction: "manipulation" }}
                    // Fix C — active-state highlight. When the caret is
                    // inside a run of this format, the button gets a
                    // solid orange ring + tinted background so the user
                    // can see at a glance which formats are already
                    // applied. `t.accent` (static per-tool accent for
                    // Hierarchy / Insert Form) is preserved as a
                    // fallback for tools that are never "active".
                    className={`shrink-0 w-9 h-9 mx-0.5 my-0.5 rounded-lg flex items-center justify-center ${
                      activeNow
                        ? "ring-2 ring-orange-500 bg-orange-500/15"
                        : (t.accent ? "ring-1 ring-orange-500/50" : "")
                    } hover:bg-white/10 active:scale-95 transition ${t.className || ""}`}
                  >
                    <Icon size={16} className={t.stub ? stubColor : iconColor} />
                  </button>
                );
              })}
              {gi < groups.length - 1 && (
                <div className={`${isH ? "w-px h-6 mx-1" : "h-px w-6 my-1"} ${isDark ? "bg-white/10" : "bg-gray-300"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ---- Forms Pack picker (read-only preview) ---- */}
      {pickerOpen === "forms" && (
        <div className="fixed inset-0 z-[2147483001] flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={() => setPickerOpen(null)}>
          <div
            className={`w-full max-w-md rounded-2xl border ${isDark ? "bg-slate-900 border-white/10 text-slate-100" : "bg-white border-gray-200 text-slate-900"} p-4`}
            onClick={(e) => e.stopPropagation()}
            data-testid="floating-rte-forms-picker"
          >
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={18} className="text-orange-400" />
              <h3 className="text-base font-semibold">Insert Form</h3>
            </div>
            <p className={`text-xs mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Live from your Tile / Forms Packs. Insertion lands in Module 2.
            </p>
            <div className="max-h-72 overflow-y-auto -mx-1">
              {FormsPackAdapter.listPacks().map(p => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`floating-rte-forms-pack-${p.id}`}
                  onClick={() => toast.info(`"${p.name}" — insertion arrives with Module 2`, { duration: 2000 })}
                  className={`w-full text-left px-3 py-2 mx-1 rounded-lg flex items-center gap-2 hover:bg-white/5`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.accent }} />
                  <span className="text-sm">{p.name}</span>
                  <span className={`ml-auto text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{p.tileCount} tiles</span>
                </button>
              ))}
              {FormsPackAdapter.listPacks().length === 0 && (
                <div className={`text-sm text-center py-6 ${isDark ? "text-slate-500" : "text-slate-500"}`}>No Forms Packs available.</div>
              )}
            </div>
            <div className="flex justify-end mt-3">
              <button
                type="button"
                data-testid="floating-rte-forms-picker-close"
                onClick={() => setPickerOpen(null)}
                className="px-3 py-1.5 rounded-lg text-sm bg-white/10 hover:bg-white/15"
              >Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Hierarchy picker (read-only preview) ---- */}
      {pickerOpen === "hierarchy" && (
        <HierarchyPreviewDialog isDark={isDark} onClose={() => setPickerOpen(null)} />
      )}

      {/* ---- Aa Text Appearance dropdown (floating overlay) ---- */}
      {aaPanel && (
        <AppearancePanel
          anchor={aaPanel.anchor}
          orientation={orientation}
          onClose={() => setAaPanel(null)}
        />
      )}

      {/* ---- Highlighter (text background) palette ---- */}
      {hlPanel && (
        <HighlightPanel
          anchor={hlPanel.anchor}
          orientation={orientation}
          onClose={() => setHlPanel(null)}
        />
      )}

      {/* ---- Alignment palette (L / C / R) ---- */}
      {alignPanel && (
        <AlignPanel
          anchor={alignPanel.anchor}
          orientation={orientation}
          onClose={() => setAlignPanel(null)}
        />
      )}
    </>
  );

  // Portal to document.body so the toolbar escapes NoteModal's Radix
  // Dialog stacking context. Without this, the modal's transformed /
  // isolated content wrapper becomes the toolbar's containing block for
  // z-index purposes, which is exactly what allowed underlying editor
  // controls to swallow taps intended for the toolbar.
  if (typeof document === "undefined") return toolbar;
  return createPortal(toolbar, document.body);
}

// --------------------------------------------------------------------------
// Hierarchy preview — reads notes from IndexedDB in read-only mode and
// projects the live tree via HierarchyAdapter.buildTree.  No writes.
// --------------------------------------------------------------------------
function HierarchyPreviewDialog({ isDark, onClose }) {
  const [tree, setTree] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { StorageService } = await import("../../storage/storageService");
        const notes = await StorageService.getAllNotes();
        setTree(HierarchyAdapter.buildTree(notes));
      } catch {
        setTree([]);
      }
    })();
  }, []);

  const renderNode = (node, depth = 0) => (
    <div key={`${depth}-${node.name}`} className="text-sm">
      <div
        className="flex items-center gap-2 py-1"
        style={{ paddingLeft: depth * 14 }}
        data-testid={`floating-rte-hierarchy-node-${node.name}`}
      >
        <Network size={12} className="text-orange-400 shrink-0" />
        <span>{node.name}</span>
      </div>
      {node.children.map(child => renderNode(child, depth + 1))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1001] flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className={`w-full max-w-md rounded-2xl border ${isDark ? "bg-slate-900 border-white/10 text-slate-100" : "bg-white border-gray-200 text-slate-900"} p-4`}
        onClick={(e) => e.stopPropagation()}
        data-testid="floating-rte-hierarchy-picker"
      >
        <div className="flex items-center gap-2 mb-3">
          <Network size={18} className="text-orange-400" />
          <h3 className="text-base font-semibold">Hierarchy Preview</h3>
        </div>
        <p className={`text-xs mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Live categories from your notes. Placement lands in Module 2.
        </p>
        <div className="max-h-72 overflow-y-auto -mx-1 pl-1">
          {tree === null && <div className="text-xs opacity-70 py-4 text-center">Loading…</div>}
          {tree && tree.length === 0 && <div className="text-xs opacity-70 py-4 text-center">No categories yet.</div>}
          {tree && tree.map(node => renderNode(node))}
        </div>
        <div className="flex justify-end mt-3">
          <button
            type="button"
            data-testid="floating-rte-hierarchy-picker-close"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm bg-white/10 hover:bg-white/15"
          >Close</button>
        </div>
      </div>
    </div>
  );
}


// --------------------------------------------------------------------------
// AppearancePanel — floating smoked-glass dropdown anchored to the Aa
// toolbar button. Pure overlay: portaled to body, position: fixed,
// does NOT alter the toolbar/editor layout.
// --------------------------------------------------------------------------
const AA_SIZE_OPTIONS = [
  { className: "ir-size-sm", label: "Small",       sample: "Aa" },
  { className: "ir-size-md", label: "Normal",      sample: "Aa" },
  { className: "ir-size-lg", label: "Large",       sample: "Aa" },
  { className: "ir-size-xl", label: "Extra Large", sample: "Aa" },
];
const AA_COLOR_OPTIONS = [
  { className: null,              name: "default", title: "Default",  hex: null       },
  { className: "ir-color-white",  name: "white",   title: "White",    hex: "#ffffff"  },
  { className: "ir-color-gray",   name: "gray",    title: "Gray",     hex: "#9ca3af"  },
  { className: "ir-color-red",    name: "red",     title: "Red",      hex: "#ef4444"  },
  { className: "ir-color-orange", name: "orange",  title: "Orange",   hex: "#f97316"  },
  { className: "ir-color-amber",  name: "amber",   title: "Amber",    hex: "#f59e0b"  },
  { className: "ir-color-yellow", name: "yellow",  title: "Yellow",   hex: "#eab308"  },
  { className: "ir-color-lime",   name: "lime",    title: "Lime",     hex: "#84cc16"  },
  { className: "ir-color-green",  name: "green",   title: "Green",    hex: "#10b981"  },
  { className: "ir-color-teal",   name: "teal",    title: "Teal",     hex: "#14b8a6"  },
  { className: "ir-color-cyan",   name: "cyan",    title: "Cyan",     hex: "#06b6d4"  },
  { className: "ir-color-sky",    name: "sky",     title: "Sky",      hex: "#0ea5e9"  },
  { className: "ir-color-blue",   name: "blue",    title: "Blue",     hex: "#3b82f6"  },
  { className: "ir-color-indigo", name: "indigo",  title: "Indigo",   hex: "#6366f1"  },
  { className: "ir-color-violet", name: "violet",  title: "Violet",   hex: "#a855f7"  },
  { className: "ir-color-pink",   name: "pink",    title: "Pink",     hex: "#ec4899"  },
];

// Session-scoped memory of the last position the user DRAGGED the Aa
// panel to. Lives at module scope so re-opening Aa within the same
// editor session restores the dragged location instead of snapping
// back over the user's text. Null = use the auto-computed anchor pos.
let aaLastUserPos = null;

function AppearancePanel({ anchor, orientation, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(() => (
    aaLastUserPos
      ? { left: aaLastUserPos.left, top: aaLastUserPos.top, ready: true }
      : { left: 0, top: 0, ready: false }
  ));
  const [active, setActive] = useState({ size: null, color: null });
  // Drag state: null when idle; { startX, startY, origLeft, origTop,
  // pointerId } while a pointer-driven drag is in progress on the
  // dedicated handle. The handle is the ONLY region that initiates a
  // drag — size chips / color swatches stay as normal tap targets.
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  // Which pending `<span>` the panel currently "owns" (null = none yet
  // this session). Used by applyAppearanceChange to decide whether a
  // tap should STACK with the previous axis or RESET (orphan from an
  // Enter-clone). See the long comment on applyAppearanceChange above.
  const ownedSpanRef = useRef(null);

  // Position computation — one-shot on open; after that the panel
  // stays wherever it was placed (either auto-anchored or dragged).
  // The anchor rect captured on open becomes stale as soon as the
  // toolbar moves (e.g. Android keyboard opens / resizes the viewport),
  // so re-running compute on every resize was repositioning the panel
  // incorrectly. We now compute ONCE, then rely on `position: fixed` +
  // a visualViewport correction (see next effect) to keep the panel
  // anchored to the visible viewport regardless of what happens to the
  // underlying document, editor scroll, or ancestor transforms.
  useEffect(() => {
    if (aaLastUserPos) return; // user already moved it — respect their choice
    const panel = panelRef.current;
    if (!panel || !anchor) return;
    // Wait one frame so the panel's offsetWidth/offsetHeight are measurable.
    const run = () => {
      const pw = panel.offsetWidth;
      const ph = panel.offsetHeight;
      const vw = window.innerWidth;
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const GAP = 8;
      let left, top;
      if (orientation === "vertical") {
        left = anchor.right + GAP;
        if (left + pw > vw - 6) left = Math.max(6, anchor.left - pw - GAP);
        top = anchor.top;
        if (top + ph > vh - 6) top = Math.max(6, vh - ph - 6);
      } else {
        top = anchor.bottom + GAP;
        if (top + ph > vh - 6) top = Math.max(6, anchor.top - ph - GAP);
        left = anchor.left;
        if (left + pw > vw - 6) left = Math.max(6, vw - pw - 6);
      }
      setPos({ left, top, ready: true });
    };
    // Immediate + rAF fallback (first measurement is sometimes 0 on first paint).
    run();
    const raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [anchor, orientation]);

  // visualViewport correction — keeps the panel locked to the VISIBLE
  // viewport even when:
  //   • The user types enough text to make the editor's own content
  //     scroll (the panel must stay still — it's a floating UI control,
  //     not part of the document).
  //   • The document itself scrolls (window.scrollY changes).
  //   • The visual viewport shifts (Android keyboard, pinch-zoom).
  //   • Any ancestor of the portaled overlay has a `transform` /
  //     `filter` / `will-change` that breaks `position: fixed`'s
  //     viewport anchoring (classic bug in modal/drawer libraries).
  // We offset the panel's inline `transform: translate` by whatever
  // delta the visualViewport reports from the layout viewport. The
  // base `pos.left/top` from the compute effect remains unchanged —
  // this effect just paints the panel at the correct VISUAL position.
  const [vvOffset, setVvOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const read = () => {
      // visualViewport.offsetLeft/Top is the layout-viewport offset of
      // the visual viewport (both > 0 when the user has pinch-scrolled
      // sideways or the keyboard has pushed content up).
      const vv = window.visualViewport;
      if (!vv) { setVvOffset({ x: window.scrollX || 0, y: window.scrollY || 0 }); return; }
      setVvOffset({
        x: (vv.offsetLeft || 0) + (window.scrollX || 0) - window.scrollX,
        y: (vv.offsetTop  || 0) + (window.scrollY || 0) - window.scrollY,
      });
    };
    read();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("scroll", read);
      vv.addEventListener("resize", read);
    }
    window.addEventListener("scroll", read, true);  // capture → catch inner-scroll
    return () => {
      if (vv) {
        vv.removeEventListener("scroll", read);
        vv.removeEventListener("resize", read);
      }
      window.removeEventListener("scroll", read, true);
    };
  }, []);

  // Refresh active state continuously so the highlighted chip/swatch
  // always matches the caret's current context.
  useEffect(() => {
    const refresh = () => {
      const ed = findActiveEditor();
      if (!ed) { setActive({ size: null, color: null }); return; }
      setActive(readActiveAppearance(ed));
    };
    refresh();
    const t = setInterval(refresh, 220);
    document.addEventListener("selectionchange", refresh);
    return () => {
      clearInterval(t);
      document.removeEventListener("selectionchange", refresh);
    };
  }, []);

  // Dismiss on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const applyOnEditor = useCallback((axis, className) => {
    const ed = findActiveEditor();
    if (!ed) return;
    try { restoreStashedSelectionIfNeeded(ed); } catch {}
    applyAppearanceChange(ed, axis, className, ownedSpanRef.current);
    // Re-adopt whichever pending span now holds the caret so a
    // subsequent tap in the same panel session STACKS with it
    // (preserving the axis the user just set) instead of being
    // treated as an orphan. Works for every path:
    //   • Textarea promote → adopts the freshly-created span.
    //   • In-place mutate  → re-adopts the same span.
    //   • Emerge-to-sibling → adopts the new sibling span.
    requestAnimationFrame(() => {
      const found = (() => {
        const sel2 = window.getSelection && window.getSelection();
        if (!sel2 || sel2.rangeCount === 0) return null;
        const r = sel2.getRangeAt(0);
        if (!ed.contains(r.startContainer)) return null;
        let n = r.startContainer;
        if (n && n.nodeType === Node.TEXT_NODE) n = n.parentElement;
        while (n && n !== ed) {
          if (n.tagName === "SPAN" && n.classList && n.classList.contains(PENDING_INLINE_CLASS)) return n;
          n = n.parentElement;
        }
        return null;
      })();
      ownedSpanRef.current = found;
    });
    try { stashSelection(); } catch {}
  }, []);

  // ---- Drag handlers for the Move handle ---------------------------------
  // Coordinate math — because the panel paints at
  //   visualX = pos.left + vvOffset.x
  //   visualY = pos.top  + vvOffset.y
  // we must do the drag arithmetic in VISUAL space (where the user's
  // finger lives). We record the panel's visual-rect at drag start and
  // the pointer's clientX/Y (also visual), compute the new visual
  // position from finger delta, clamp it to the visible viewport, then
  // CONVERT back to layout coords by subtracting vvOffset before
  // writing to `pos`. This keeps the finger locked to the same point
  // on the Move handle for the entire drag, regardless of whether the
  // keyboard is open, the viewport has scrolled, or an ancestor
  // transform is in play.
  const onDragStart = useCallback((e) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect(); // VISUAL rect
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origVisualLeft: rect.left,
      origVisualTop: rect.top,
      pointerId: e.pointerId,
    };
    setIsDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const panel = panelRef.current;
    if (!panel) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const vw = window.innerWidth;
    const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    // Compute new VISUAL position (where the finger wants the panel).
    let newVisualLeft = d.origVisualLeft + dx;
    let newVisualTop  = d.origVisualTop + dy;
    // Clamp in visual space. Loose enough to let the user park the
    // panel anywhere in the visible viewport, strict enough that the
    // Move handle itself is always reachable.
    const EDGE_H = 60; // keep at least this much of the panel on-screen horizontally
    const EDGE_T = 2;  // may almost touch the top of the visible viewport
    const EDGE_B = 44; // keep the drag handle (~top 36px) visible if parked near the bottom
    newVisualLeft = Math.min(vw - EDGE_H, Math.max(EDGE_H - pw, newVisualLeft));
    newVisualTop  = Math.min(vh - EDGE_B, Math.max(EDGE_T, newVisualTop));
    // Convert back to layout coords for CSS left/top.
    setPos({
      left: newVisualLeft - vvOffset.x,
      top:  newVisualTop  - vvOffset.y,
      ready: true,
    });
    e.stopPropagation();
    e.preventDefault();
  }, [vvOffset.x, vvOffset.y]);

  const onDragEnd = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    try { e.currentTarget.releasePointerCapture(d.pointerId); } catch {}
    // Persist the final BASE (layout-coord) pos so re-opening the panel
    // in this session restores the user's chosen spot even if vvOffset
    // changes meanwhile. The CSS will add vvOffset back on paint.
    const panel = panelRef.current;
    if (panel) {
      const rect = panel.getBoundingClientRect();
      aaLastUserPos = {
        left: rect.left - vvOffset.x,
        top:  rect.top  - vvOffset.y,
      };
    }
    dragRef.current = null;
    setIsDragging(false);
    e.stopPropagation();
  }, [vvOffset.x, vvOffset.y]);

  const backdrop = null; // integrated into the overlay wrap below

  const panel = (
    <div
      ref={panelRef}
      className="ir-aa-panel"
      data-testid="floating-rte-aa-panel"
      style={{
        left: pos.left,
        top: pos.top,
        // Correct for any ancestor-transform / visualViewport offset so
        // the panel always paints at the user's chosen VISUAL coords.
        transform: `translate3d(${vvOffset.x}px, ${vvOffset.y}px, 0)`,
        visibility: pos.ready ? "visible" : "hidden",
      }}
      // Stop propagation on EVERY interaction event so taps inside the
      // panel never reach the wrap (which would otherwise dismiss) and
      // never reach underlying UI. `pointer-events: auto` on the panel
      // guarantees events are delivered here regardless of any inherited
      // `pointer-events: none` from parent stacking contexts.
      onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e)       => { e.stopPropagation(); }}
    >
      {/* Dedicated Move/drag handle — the ONLY region that initiates
          a panel drag. Size chips & color swatches below remain normal
          tap targets. The handle uses pointer-capture so a finger that
          slides off still drives the drag. */}
      <div
        data-testid="floating-rte-aa-drag-handle"
        className={`ir-aa-drag-handle ${isDragging ? "dragging" : ""}`}
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        onClick={(e) => e.stopPropagation()}
        title="Drag to move"
      >
        <span className="ir-aa-drag-handle-dots" aria-hidden="true">
          <span /><span /><span />
        </span>
        <span>Move</span>
      </div>

      <div className="ir-aa-section-label">Size</div>
      <div className="ir-aa-row" data-testid="floating-rte-aa-size-row">
        {AA_SIZE_OPTIONS.map((s) => (
          <button
            key={s.className}
            type="button"
            data-testid={`floating-rte-aa-size-${s.className.replace("ir-size-", "")}`}
            className={`ir-aa-size-chip ${active.size === s.className ? "active" : ""}`}
            onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onTouchStart={(e)  => { e.stopPropagation(); }}
            onTouchEnd={(e)    => { e.stopPropagation(); }}
            onClick={(e)       => { e.stopPropagation(); applyOnEditor("size", s.className); }}
            title={s.label}
          >
            <div
              className="ir-aa-size-chip-sample"
              style={{
                fontSize: s.className === "ir-size-sm" ? "0.85em"
                        : s.className === "ir-size-md" ? "1em"
                        : s.className === "ir-size-lg" ? "1.25em"
                        : "1.5em",
              }}
            >{s.sample}</div>
            <div className="ir-aa-size-chip-label">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="ir-aa-section-label">Color</div>
      <div className="ir-aa-color-grid" data-testid="floating-rte-aa-color-grid">
        {AA_COLOR_OPTIONS.map((c) => (
          <button
            key={c.name}
            type="button"
            data-testid={`floating-rte-aa-color-${c.name}`}
            data-color={c.name}
            title={c.title}
            className={`ir-aa-color-swatch ${
              (c.className === null && !active.color) || (c.className && active.color === c.className)
                ? "active" : ""
            }`}
            style={c.hex ? { background: c.hex } : undefined}
            onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onTouchStart={(e)  => { e.stopPropagation(); }}
            onTouchEnd={(e)    => { e.stopPropagation(); }}
            onClick={(e)       => { e.stopPropagation(); applyOnEditor("color", c.className); }}
          />
        ))}
      </div>

      <button
        type="button"
        data-testid="floating-rte-aa-clear-format"
        className="ir-aa-clear-btn"
        onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
        onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
        onTouchStart={(e)  => { e.stopPropagation(); }}
        onTouchEnd={(e)    => { e.stopPropagation(); }}
        onClick={(e) => {
          e.stopPropagation();
          const ed = findActiveEditor();
          if (!ed) return;
          try { restoreStashedSelectionIfNeeded(ed); } catch {}
          runExecCommand(ed, "removeFormat");
          // Clear Aa appearance too. `removeFormat` strips B/I/U/S/font
          // but ignores our custom `<span class="ir-size-* ir-color-*">`
          // wrappers. Walk ancestors from the caret and strip the
          // appearance classes; if the span ends up with no classes,
          // drop the class attribute entirely so the active-state
          // detector doesn't keep lighting Aa.
          try {
            const sel = window.getSelection && window.getSelection();
            if (sel && sel.rangeCount && ed.contains(sel.getRangeAt(0).startContainer)) {
              let node = sel.getRangeAt(0).startContainer;
              if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
              while (node && node !== ed) {
                if (node.tagName === "SPAN" && node.classList) {
                  AA_SIZE_CLASSES.forEach((c) => node.classList.remove(c));
                  AA_COLOR_CLASSES.forEach((c) => node.classList.remove(c));
                  if (node.classList.length === 0) node.removeAttribute("class");
                }
                node = node.parentElement;
              }
              // Also strip the document-level size class from the editor
              // root (post-v205 Aa Size lives here).
              if (ed.classList) {
                AA_SIZE_CLASSES.forEach((c) => ed.classList.remove(c));
              }
              // Reset the owned-span ref so a subsequent Aa pick starts
              // fresh rather than stacking with the now-stripped span.
              ownedSpanRef.current = null;
              ed.dispatchEvent(new Event("input", { bubbles: true }));
            }
          } catch {}
          try { stashSelection(); } catch {}
        }}
      >Clear formatting</button>
    </div>
  );

  // Single full-viewport overlay wrap owns ALL hit-testing while the
  // panel is open. Taps on the panel are handled by the panel (with
  // stopPropagation). Taps OUTSIDE the panel but INSIDE this wrap close
  // the panel. Nothing underneath — editor, toolbar, tile accent colors,
  // category rows, modal body — can receive touches while the panel is
  // open. This fixes the "touch-through" bug where chip taps were
  // bleeding through to underlying controls.
  const overlay = (
    <div
      data-testid="floating-rte-aa-overlay"
      className="ir-aa-overlay"
      // Dismiss on wrap taps (outside the panel). Stop propagation on
      // every pointer/touch/mouse event so NOTHING underneath receives
      // the event. The wrap sits at the top of the stacking order via
      // its CSS class.
      onPointerDown={(e) => { e.stopPropagation(); }}
      onMouseDown={(e)   => { e.stopPropagation(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      {panel}
    </div>
  );

  if (typeof document === "undefined") return overlay;
  return createPortal(overlay, document.body);
}


// --------------------------------------------------------------------------
// HighlightPanel — a small floating palette for the Highlighter button.
// Same portal + overlay + touch-isolation pattern as AppearancePanel,
// but only shows a color grid + a Clear button.
// --------------------------------------------------------------------------
const HL_OPTIONS = [
  { className: null,            name: "none",    title: "No highlight", hex: null       },
  { className: "ir-hl-white",   name: "white",   title: "White",         hex: "rgba(255,255,255,0.35)" },
  { className: "ir-hl-gray",    name: "gray",    title: "Gray",          hex: "rgba(156,163,175,0.45)" },
  { className: "ir-hl-red",     name: "red",     title: "Red",           hex: "rgba(239,68,68,0.40)" },
  { className: "ir-hl-orange",  name: "orange",  title: "Orange",        hex: "rgba(249,115,22,0.45)" },
  { className: "ir-hl-amber",   name: "amber",   title: "Amber",         hex: "rgba(245,158,11,0.50)" },
  { className: "ir-hl-yellow",  name: "yellow",  title: "Yellow",        hex: "rgba(234,179,8,0.55)" },
  { className: "ir-hl-lime",    name: "lime",    title: "Lime",          hex: "rgba(132,204,22,0.50)" },
  { className: "ir-hl-green",   name: "green",   title: "Green",         hex: "rgba(16,185,129,0.40)" },
  { className: "ir-hl-teal",    name: "teal",    title: "Teal",          hex: "rgba(20,184,166,0.40)" },
  { className: "ir-hl-cyan",    name: "cyan",    title: "Cyan",          hex: "rgba(6,182,212,0.40)" },
  { className: "ir-hl-sky",     name: "sky",     title: "Sky",           hex: "rgba(14,165,233,0.40)" },
  { className: "ir-hl-blue",    name: "blue",    title: "Blue",          hex: "rgba(59,130,246,0.40)" },
  { className: "ir-hl-indigo",  name: "indigo",  title: "Indigo",        hex: "rgba(99,102,241,0.40)" },
  { className: "ir-hl-violet",  name: "violet",  title: "Violet",        hex: "rgba(168,85,247,0.40)" },
  { className: "ir-hl-pink",    name: "pink",    title: "Pink",          hex: "rgba(236,72,153,0.40)" },
  { className: "ir-hl-rose",    name: "rose",    title: "Rose",          hex: "rgba(244,63,94,0.40)" },
];

function HighlightPanel({ anchor, orientation, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });
  const [activeHl, setActiveHl] = useState(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !anchor) return;
    const run = () => {
      const pw = panel.offsetWidth;
      const ph = panel.offsetHeight;
      const vw = window.innerWidth;
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const GAP = 8;
      let left, top;
      if (orientation === "vertical") {
        left = anchor.right + GAP;
        if (left + pw > vw - 6) left = Math.max(6, anchor.left - pw - GAP);
        top = anchor.top;
        if (top + ph > vh - 6) top = Math.max(6, vh - ph - 6);
      } else {
        top = anchor.bottom + GAP;
        if (top + ph > vh - 6) top = Math.max(6, anchor.top - ph - GAP);
        left = anchor.left;
        if (left + pw > vw - 6) left = Math.max(6, vw - pw - 6);
      }
      setPos({ left, top, ready: true });
    };
    run();
    const raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [anchor, orientation]);

  const [vvOffset, setVvOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const read = () => {
      const vv = window.visualViewport;
      if (!vv) { setVvOffset({ x: 0, y: 0 }); return; }
      setVvOffset({ x: vv.offsetLeft || 0, y: vv.offsetTop || 0 });
    };
    read();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("scroll", read);
      vv.addEventListener("resize", read);
    }
    window.addEventListener("scroll", read, true);
    return () => {
      if (vv) {
        vv.removeEventListener("scroll", read);
        vv.removeEventListener("resize", read);
      }
      window.removeEventListener("scroll", read, true);
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      const ed = findActiveEditor();
      setActiveHl(ed ? readActiveHighlight(ed) : null);
    };
    refresh();
    const t = setInterval(refresh, 220);
    document.addEventListener("selectionchange", refresh);
    return () => { clearInterval(t); document.removeEventListener("selectionchange", refresh); };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const applyOnEditor = useCallback((className) => {
    const ed = findActiveEditor();
    if (!ed) return;
    try { restoreStashedSelectionIfNeeded(ed); } catch {}
    applyHighlight(ed, className);
    try { stashSelection(); } catch {}
  }, []);

  const panel = (
    <div
      ref={panelRef}
      className="ir-hl-panel"
      data-testid="floating-rte-hl-panel"
      style={{
        left: pos.left,
        top: pos.top,
        transform: `translate3d(${vvOffset.x}px, ${vvOffset.y}px, 0)`,
        visibility: pos.ready ? "visible" : "hidden",
      }}
      onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e)       => { e.stopPropagation(); }}
    >
      <div className="ir-hl-grid" data-testid="floating-rte-hl-grid">
        {HL_OPTIONS.map((h) => (
          <button
            key={h.name}
            type="button"
            data-testid={`floating-rte-hl-${h.name}`}
            data-color={h.name}
            title={h.title}
            className={`ir-hl-swatch ${
              (h.className === null && !activeHl) || (h.className && activeHl === h.className)
                ? "active" : ""
            }`}
            style={h.hex ? { background: h.hex } : undefined}
            onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
            onTouchStart={(e)  => { e.stopPropagation(); }}
            onTouchEnd={(e)    => { e.stopPropagation(); }}
            onClick={(e)       => { e.stopPropagation(); applyOnEditor(h.className); }}
          />
        ))}
      </div>
      <button
        type="button"
        data-testid="floating-rte-hl-clear"
        className="ir-hl-clear-btn"
        onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
        onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
        onTouchStart={(e)  => { e.stopPropagation(); }}
        onTouchEnd={(e)    => { e.stopPropagation(); }}
        onClick={(e) => {
          e.stopPropagation();
          const ed = findActiveEditor();
          if (!ed) return;
          try { restoreStashedSelectionIfNeeded(ed); } catch {}
          const sel = window.getSelection && window.getSelection();
          if (sel && sel.rangeCount && ed.contains(sel.getRangeAt(0).startContainer)) {
            const r = sel.getRangeAt(0);
            if (r.collapsed) {
              let node = r.startContainer;
              if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
              while (node && node !== ed) {
                if (node.tagName === "SPAN" && node.classList) {
                  HL_CLASSES.forEach((c) => node.classList.remove(c));
                  if (node.classList.length === 0) node.removeAttribute("class");
                }
                node = node.parentElement;
              }
            } else {
              const container = r.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
                ? r.commonAncestorContainer : r.commonAncestorContainer.parentElement;
              if (container) {
                container.querySelectorAll('span').forEach((s) => {
                  if (!r.intersectsNode(s)) return;
                  HL_CLASSES.forEach((c) => s.classList.remove(c));
                  if (s.classList.length === 0) s.removeAttribute("class");
                });
              }
            }
            ed.dispatchEvent(new Event("input", { bubbles: true }));
          }
          try { stashSelection(); } catch {}
        }}
      >Clear highlight</button>
    </div>
  );

  const overlay = (
    <div
      data-testid="floating-rte-hl-overlay"
      className="ir-aa-overlay"
      onPointerDown={(e) => { e.stopPropagation(); }}
      onMouseDown={(e)   => { e.stopPropagation(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      {panel}
    </div>
  );

  if (typeof document === "undefined") return overlay;
  return createPortal(overlay, document.body);
}


// --------------------------------------------------------------------------
// AlignPanel — small floating palette for Left / Center / Right block
// alignment. Mirrors HighlightPanel's architecture 1-for-1:
//   • portaled to document.body inside an ir-aa-overlay touch wrap
//   • position anchored to the toolbar button rect
//   • visualViewport offset applied via translate3d
//   • keyboard-safe (visualViewport.height, not window.innerHeight)
//   • follows the toolbar when the toolbar moves (anchor is read on open;
//     the overlay auto-dismisses on outside taps, same as the HL panel)
//   • respects horizontal/vertical toolbar orientation
//
// Positioning preference: ABOVE the toolbar in horizontal mode (per
// user's request), with a graceful fall-through to BELOW when there is
// insufficient room overhead. In vertical mode the panel sits to the
// side of the toolbar (same rule as the HL panel) because "above" has
// no geometric meaning there.
// --------------------------------------------------------------------------
const ALIGN_OPTIONS = [
  { className: "ir-align-left",   name: "left",   title: "Align left",   Icon: AlignLeft   },
  { className: "ir-align-center", name: "center", title: "Align center", Icon: AlignCenter },
  { className: "ir-align-right",  name: "right",  title: "Align right",  Icon: AlignRight  },
];

function AlignPanel({ anchor, orientation, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });
  const [activeAlign, setActiveAlign] = useState(null);
  // Dual-mode state (Step A only — UI shell).
  //   • "block"  (default) → the three alignment buttons apply BLOCK-level
  //      text-align via `.ir-align-left/center/right` on the caret's block
  //      ancestor. This is the fully-working behaviour.
  //   • "inline" → the three buttons still render in the same positions,
  //      but their taps are STUBBED with a toast ("Inline alignment coming
  //      soon"). No editor content is mutated in INLINE mode. The
  //      underlying content-model work is pending explicit approval
  //      (Step B).
  // The mode toggle is a single-icon button in the top-left corner of
  // the panel. Icon reflects CURRENT mode: Rows3 (= block) ⇄
  // ArrowLeftRight (= inline). Tapping flips the mode and keeps the
  // panel open. BLOCK remains the default every time the panel opens.
  const [mode, setMode] = useState("block");

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !anchor) return;
    const run = () => {
      const pw = panel.offsetWidth;
      const ph = panel.offsetHeight;
      const vw = window.innerWidth;
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const GAP = 8;
      let left, top;
      if (orientation === "vertical") {
        // Side-of-toolbar placement (mirrors HL panel in vertical mode).
        left = anchor.right + GAP;
        if (left + pw > vw - 6) left = Math.max(6, anchor.left - pw - GAP);
        top = anchor.top;
        if (top + ph > vh - 6) top = Math.max(6, vh - ph - 6);
      } else {
        // PREFER ABOVE the toolbar — user requirement. Fall back to below
        // only if the panel would clip above the visualViewport top.
        top = anchor.top - ph - GAP;
        if (top < 6) {
          const belowTop = anchor.bottom + GAP;
          top = (belowTop + ph <= vh - 6) ? belowTop : Math.max(6, vh - ph - 6);
        }
        left = anchor.left;
        if (left + pw > vw - 6) left = Math.max(6, vw - pw - 6);
      }
      setPos({ left, top, ready: true });
    };
    run();
    const raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [anchor, orientation]);

  const [vvOffset, setVvOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const read = () => {
      const vv = window.visualViewport;
      if (!vv) { setVvOffset({ x: 0, y: 0 }); return; }
      setVvOffset({ x: vv.offsetLeft || 0, y: vv.offsetTop || 0 });
    };
    read();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("scroll", read);
      vv.addEventListener("resize", read);
    }
    window.addEventListener("scroll", read, true);
    return () => {
      if (vv) {
        vv.removeEventListener("scroll", read);
        vv.removeEventListener("resize", read);
      }
      window.removeEventListener("scroll", read, true);
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      const ed = findActiveEditor();
      setActiveAlign(ed ? readActiveAlign(ed) : null);
    };
    refresh();
    const t = setInterval(refresh, 220);
    document.addEventListener("selectionchange", refresh);
    return () => { clearInterval(t); document.removeEventListener("selectionchange", refresh); };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const applyOnEditor = useCallback((className) => {
    const ed = findActiveEditor();
    if (!ed) return;
    try { restoreStashedSelectionIfNeeded(ed); } catch {}
    applyAlign(ed, className);
    try { stashSelection(); } catch {}
    // Refresh local active-state immediately so the swatch state updates
    // without waiting for the 220ms poll.
    setActiveAlign(readActiveAlign(ed));
  }, []);

  // INLINE mode handler — inserts a fresh 3-zone row at the caret, or
  // (if the caret is already inside a row) jumps to the requested cell
  // without touching existing content. See `insertInlineRow` /
  // `moveCaretToCell`. Side-effect free for all other content.
  const applyInline = useCallback((pos) => {
    const ed = findActiveEditor();
    if (!ed) return;
    try { restoreStashedSelectionIfNeeded(ed); } catch {}
    // If caret is already in a row cell, jump; otherwise insert new row.
    const sel = window.getSelection && window.getSelection();
    const node = sel && sel.rangeCount ? sel.getRangeAt(0).startContainer : null;
    const existingRow = node ? findInlineRow(ed, node) : null;
    if (existingRow) {
      const target = existingRow.querySelector(`[data-pos="${pos}"]`);
      moveCaretToCell(target);
    } else {
      insertInlineRow(ed, pos);
    }
    try { stashSelection(); } catch {}
  }, []);

  const panel = (
    <div
      ref={panelRef}
      className={`ir-align-panel ${mode === "inline" ? "mode-inline" : "mode-block"}`}
      data-mode={mode}
      data-testid="floating-rte-align-panel"
      style={{
        left: pos.left,
        top: pos.top,
        transform: `translate3d(${vvOffset.x}px, ${vvOffset.y}px, 0)`,
        visibility: pos.ready ? "visible" : "hidden",
      }}
      onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e)       => { e.stopPropagation(); }}
    >
      {/* Mode toggle header — Rows3 (block) ⇄ ArrowLeftRight (inline).
          Lives in the top-left of the panel. Keeps the panel open on
          toggle. BLOCK is always the opening default. */}
      <div className="ir-align-head">
        <button
          type="button"
          className="ir-align-mode-btn"
          data-testid="floating-rte-align-mode-toggle"
          data-mode={mode}
          aria-pressed={mode === "inline"}
          title={mode === "inline" ? "Switch to Block alignment" : "Switch to Inline alignment"}
          aria-label={mode === "inline" ? "Switch to Block alignment" : "Switch to Inline alignment"}
          onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
          onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
          onTouchStart={(e)  => { e.stopPropagation(); }}
          onTouchEnd={(e)    => { e.stopPropagation(); }}
          onClick={(e) => {
            e.stopPropagation();
            setMode((m) => (m === "block" ? "inline" : "block"));
          }}
        >
          {mode === "block" ? <Rows3 size={14} /> : <ArrowLeftRight size={14} />}
        </button>
        <span className="ir-align-mode-chip" data-testid="floating-rte-align-mode-chip">
          {mode === "inline" ? "Inline" : "Block"}
        </span>
      </div>

      <div className="ir-align-row" data-testid="floating-rte-align-row">
        {ALIGN_OPTIONS.map(({ className, name, title, Icon }) => {
          const isInline = mode === "inline";
          const btnTitle = isInline
            ? `Inline ${title.replace(/^Align\s*/i, "")}`
            : title;
          return (
            <button
              key={name}
              type="button"
              data-testid={`floating-rte-align-${name}`}
              data-align={name}
              data-mode={mode}
              title={btnTitle}
              aria-label={btnTitle}
              className={`ir-align-btn ${!isInline && activeAlign === className ? "active" : ""} ${isInline ? "inline" : ""}`}
              onPointerDown={(e) => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
              onMouseDown={(e)   => { stashSelection(); e.stopPropagation(); e.preventDefault(); }}
              onTouchStart={(e)  => { e.stopPropagation(); }}
              onTouchEnd={(e)    => { e.stopPropagation(); }}
              onClick={(e) => {
                e.stopPropagation();
                if (isInline) applyInline(name);
                else applyOnEditor(className);
              }}
            >
              <Icon size={16} />
            </button>
          );
        })}
      </div>
    </div>
  );

  const overlay = (
    <div
      data-testid="floating-rte-align-overlay"
      className="ir-aa-overlay"
      onPointerDown={(e) => { e.stopPropagation(); }}
      onMouseDown={(e)   => { e.stopPropagation(); }}
      onTouchStart={(e)  => { e.stopPropagation(); }}
      onTouchEnd={(e)    => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      {panel}
    </div>
  );

  if (typeof document === "undefined") return overlay;
  return createPortal(overlay, document.body);
}
